import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  ElementRef,
  HostListener,
  inject,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Subscription, timer } from 'rxjs';

// PrimeNG
import { ConfirmationService, PrimeIcons } from 'primeng/api';
import { Button } from 'primeng/button';
import { Card } from 'primeng/card';
import { DatePicker } from 'primeng/datepicker';
import { DropdownModule } from 'primeng/dropdown';
import { FloatLabel } from 'primeng/floatlabel';
import { InputText } from 'primeng/inputtext';
import { MenuModule } from 'primeng/menu';
import { Table, TableModule } from 'primeng/table';
import { TabsModule } from 'primeng/tabs';
import { TabViewModule } from 'primeng/tabview';

// Services & Models
import { DateService, UserService } from '@tw/services';
import {
  AutoComplete,
  AutoCompleteCompleteEvent,
  AutoCompleteSelectEvent,
} from 'primeng/autocomplete';
import { AutoFocusModule } from 'primeng/autofocus';
import { CalendarModule } from 'primeng/calendar';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { ConfirmPopupModule } from 'primeng/confirmpopup';
import { DialogModule } from 'primeng/dialog';
import { IconField } from 'primeng/iconfield';
import { InputIcon } from 'primeng/inputicon';
import { ProgressBarModule } from 'primeng/progressbar';
import { SelectChangeEvent, SelectModule } from 'primeng/select';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import {
  AttendanceAdjustmentTypeEnum,
  LeaveTypeEnum,
  MyAdherenceReportResponse,
  RoleTaskMappingResponse,
  TimeEntry,
  UserDetails,
  UserEvent,
} from 'src/shared/models/common';
import {
  PermissionAction,
  PermissionSubject,
} from 'src/shared/models/permissions';
import { CommonService } from 'src/shared/services/common.service';
import { ReportService } from 'src/shared/services/reports.service';
import { TrackerService } from 'src/shared/services/tracker.service';

import { ReqTimerEntryComponent } from './req-timer-entry/req-timer-entry.component';
import {
  FullDayLeaveTypeEnum,
  HalfDayLeaveTypeEnum,
  TOAST_MESSAGES,
} from './track-utils';
import { TrackService } from './track.service';
@Component({
  selector: 'tw-track',
  standalone: true,
  templateUrl: './track.component.html',
  styleUrl: './track.component.scss',
  imports: [
    Card,
    DropdownModule,
    InputText,
    FloatLabel,
    ReactiveFormsModule,
    FormsModule,
    Button,
    TableModule,
    TabsModule,
    CommonModule,
    DatePicker,
    TabViewModule,
    MenuModule,
    AutoComplete,
    ConfirmDialog,
    ToastModule,
    ConfirmPopupModule,
    TooltipModule,
    SelectModule,
    IconField,
    InputIcon,
    ProgressBarModule,
    CalendarModule,
    AutoFocusModule,
    DialogModule,
    ReqTimerEntryComponent,
  ],
  providers: [ConfirmationService],
})
export class TrackComponent implements OnInit, OnDestroy {
  public PrimeIcons = PrimeIcons;
  TOAST_MESSAGES = TOAST_MESSAGES;
  userMetadata = computed(() => this.userService.userMetadata());
  userDetails = computed(() => this.userService.userDetails());
  @ViewChild('dt') table!: Table;

  @ViewChild('tableWrapper', { static: false }) tableWrapperRef!: ElementRef;
  @ViewChild('focus') myInputRef!: ElementRef;
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.dropdownOpen) return;

    const clickedInsideTable = this.tableWrapperRef?.nativeElement.contains(
      event.target,
    );
    if (!clickedInsideTable && this.editingRow) {
      this.cancelEdit(this.editingRow);
    }
  }
  @HostListener('window:storage', ['$event'])
  onStorage(event: StorageEvent): void {
    // Another tab cleared the running session (Stop/Save) -> stop ticking here
    // too, without saving a duplicate entry.
    if (
      event.key === 'activeTimer' &&
      event.newValue === null &&
      this.isStart
    ) {
      this.handleExternalStop();
    }
  }
  protected userService = inject(UserService);
  columns: any[] = [];
  isApprovalDialogVisible = false;
  thisWeekData = false;
  searchText = '';
  isExcelDownloading = false;
  shouldShowTooltip = false;
  myAdherenceReport: MyAdherenceReportResponse | null = null;

  manualEntryTime: {
    start: Date | null;
    end: Date | null;
    timeSpent: string | null;
  } = { start: null, end: null, timeSpent: null };
  trackEntryTable: any[] = [];
  minDate: Date = new Date(new Date().setDate(new Date().getDate() - 7));
  maxDate: Date | null = new Date();
  calMaxDate: Date | null = new Date();
  timeTickingTimeSpent!: string;
  highlightedDates: { date: Date; class: string }[] = [];
  isValidtor = false;
  trackListToday: any[] = [];
  entryForm: FormGroup;
  user: UserDetails = {} as UserDetails;
  entryBtnIcon = PrimeIcons.PENCIL;
  playIcon = PrimeIcons.PLAY;
  dropdownOpen = false;
  selectedTab = 0;
  selectedTrack = null as TimeEntry[] | null;
  clonedRow: any = {};
  filteredTimes: string[] = [];
  timerRequired = false;
  timeOptions: { label: string; value: Date }[] = [];
  fieldLabels = {
    projectId: 'Project',
    taskTypeId: 'Task',
    startTime: 'Start Time',
    endTime: 'End Time',
    timeSpent: 'Time',
    ticketId: 'Ticket Number',
    taskDate: 'Task Date',
  };
  isStart = false;
  isTimer = false;
  iconToggled = false;
  timeClass = '';
  editingRow: any = null;
  originalRowData: TimeEntry | null = null;
  originalRowlist: TimeEntry[] | [] = [];
  totalTimeSpent = '00:00:00';
  countSecond = 0;
  timerSubscription?: Subscription;
  // Time-ticking state: tracked time = banked active seconds from completed
  // segments + the live (now - segmentStart) segment. Idle windows (hourly
  // confirmation dialog, sleep gaps) are excluded by banking on freeze and
  // starting a fresh segment on resume.
  accumulatedSeconds = 0;
  segmentStart?: Date;
  confirmationInProgress = false;
  // Multi-tab coordination: exactly one tab "owns" the running timer and mutates
  // activeTimer; other tabs render the shared value read-only. Ownership is
  // claimed via a per-tab id + heartbeat so a closed owner tab can be taken over.
  tabId = '';
  isTimerOwner = false;
  private readonly TIMER_OWNER_STALE_MS = 5000;
  comments: any[] = [];
  severityClockIcon: 'success' | 'info' | 'warn' | 'danger' | null = null;
  pTooltipClockIcon = 'Start Timer';
  filteredComments: any[] = [];
  tasklist: RoleTaskMappingResponse[] = [];
  LeaveTypeEnum = LeaveTypeEnum;
  attendanceAdjustmentTypeEnum = AttendanceAdjustmentTypeEnum;
  tasklistDropdown: RoleTaskMappingResponse[] = [];
  tabData: { label: string; id: string }[] = [
    { label: 'Today', id: 'today' },
    { label: 'Previous Working Day', id: 'prevWorkingDay' },
    { label: 'Last 7 Days', id: 'week' },
  ];
  maxHours = 12;
  progressValue = 0;
  progressColor = '';
  timeDisplay = '';
  greenThreshold = 8.5;
  fieldMeta = {
    ticketId: { required: false },
    startTime: { required: true },
    endTime: { required: true },
    timeSpent: { required: true },
    comment: { required: false },
  };

  minHoursStr = this.userDetails()?.minHours || '08:30:00';
  minHours = this.timeStringToDecimalHours(this.minHoursStr);

  get IsTaskTypeMeeting() {
    const isMeetingTask = this.userMetadata()?.roleTaskMappingResponses.find(
      (task) => task.taskTypeId === this.entryForm.get('taskTypeId')?.value,
    );
    const isMeeting = isMeetingTask?.taskTypeName
      ?.toLowerCase()
      .includes('meeting');
    return isMeeting;
  }

  get isTrackSelectionDisabled(): boolean {
    return !this.selectedTrack || this.selectedTrack.length < 1;
  }
  get backDate(): Date {
    const dayCount = this.userDetails()?.dayCountForBackDatedEntry ?? 7;
    const date = new Date();
    date.setDate(date.getDate() - dayCount);
    return date;
  }

  constructor(
    private fb: FormBuilder,
    private commonService: CommonService,
    private trackerService: TrackerService,
    private confirmationService: ConfirmationService,
    private trackService: TrackService,
    private dateService: DateService,
    private reportService: ReportService,
    private ngZone: NgZone,
  ) {
    this.entryForm = this.fb.group({
      comment: [null],
      projectId: [null, Validators.required],
      ticketId: [null],
      taskTypeId: [null, Validators.required],
      startTime: [''],
      endTime: [''],
      taskDate: [new Date(), Validators.required],
      checked: new FormControl<boolean>(false),
      timeSpent: [{ value: '', disabled: false }],
    });

    this.columns = [
      {
        type: 'selection',
        width: '50%',
      },
      {
        field: 'ticketId',
        header: 'Ticket #',
        type: 'text',
        filterType: 'text',
        placeholder: 'Search by Ticket #',
        editable: true,
        isFilterable: true,
        width: '85%',
      },
      {
        field: 'projectName',
        header: 'Project & Client',
        type: 'dropdown',
        filterType: 'text',
        placeholder: 'Search by Project',
        editable: true,
        options: this.userMetadata()?.projectMappingResponses || [],
        optionLabel: 'projectId',
        isFilterable: true,
        width: '190%',
      },
      {
        field: 'taskName',
        header: 'Task',
        type: 'dropdown',
        filterType: 'text',
        placeholder: 'Search by Task',
        editable: true,
        options: this.userMetadata()?.roleTaskMappingResponses || [],
        optionLabel: 'taskTypeId',
        isFilterable: true,
        width: '120%',
      },
      {
        field: 'taskDate',
        header: 'Task Date',
        type: 'date',
        filterType: 'text',
        placeholder: 'Search by Task Date',
        isFilterable: true,
        editable: true,
        width: '105%',
      },
      {
        field: 'comment',
        header: 'Comment',
        type: 'text',
        filterType: 'text',
        placeholder: 'Search by Comment',
        editable: true,
        isFilterable: true,
        width: '160%',
      },
      {
        field: 'startTimeEndTime',
        header: 'Start - End Time',
        type: 'timeRange',
        filterType: 'text',
        placeholder: 'Search by Start - End Time',
        isCustom: true,
        editable: true,
        isFilterable: true,
        width: '150%',
      },
      {
        field: 'timeSpent',
        header: 'Time Spent',
        type: 'readonly',
        filterType: 'text',
        placeholder: 'Search by Time Spent',
        isFilterable: true,
        editable: true,
        width: '95%',
      },

      {
        field: 'actions',
        header: 'Actions',
        type: 'actions',
        isFilterable: false,
        editable: false,
        width: '80%',
      },
    ];
  }
  focusInput() {
    setTimeout(() => {
      this.myInputRef.nativeElement.focus();
    }, 0);
  }
  async ngOnInit(): Promise<void> {
    this.focusInput();
    this.tabId = crypto.randomUUID();
    this.minDate = this.backDate;

    // User details/metadata are fetched once at login (UserService bootstrap).
    // Only fetch them here if they're missing, so we don't duplicate the
    // bootstrap call on the first load of the Track page.
    await Promise.all([
      this.userDetails()
        ? Promise.resolve()
        : this.userService.resetUserDetails(),
      this.userMetadata()
        ? Promise.resolve()
        : this.userService.resetUserMetadata(),
    ]);

    // If the scheme changed since the last visit, stale tracker state in
    // localStorage (entryType/validator/activeTimer/...) can override the fresh
    // config. Clear it so init derives mode purely from the new scheme.
    const currentScheme = this.userDetails()?.timeCaptureScheme ?? '';
    if (localStorage.getItem('timeCaptureScheme') !== currentScheme) {
      [
        'entryType',
        'validator',
        'activeTimer',
        'timeEntry',
        'confirmationData',
      ].forEach((key) => localStorage.removeItem(key));
      localStorage.setItem('timeCaptureScheme', currentScheme);
    }

    const value = localStorage.getItem('entryType');
    const isValid = value !== null && value.length > 0;
    this.tasklist = this.userMetadata()?.roleTaskMappingResponses || [];
    if (
      (this.userDetails()?.timeCaptureScheme === 'TimeTicking' ||
        this.userDetails()?.timeCaptureScheme === 'Both') &&
      !isValid
    ) {
      this.isTimer = true;
      this.iconToggled = true;
      this.entryForm.get('timeSpent')?.setValue('00:00:00');
      this.entryForm.get('timeSpent')?.disable();
      this.entryForm.get('timeSpent')?.updateValueAndValidity();
    }
    if (localStorage.getItem('entryType')) {
      this.entryBtnIcon = localStorage.getItem('entryType') as string;
      if (
        (this.entryBtnIcon === PrimeIcons.PENCIL &&
          this.userDetails()?.timeCaptureScheme === 'Both') ||
        this.userDetails()?.timeCaptureScheme === 'Manual'
      ) {
        this.iconToggled = true;
        this.isTimer = true;
        this.entryForm.get('timeSpent')?.disable();
        this.entryForm.get('timeSpent')?.updateValueAndValidity();
      }
      if (
        (this.entryBtnIcon === PrimeIcons.STOPWATCH &&
          this.userDetails()?.timeCaptureScheme === 'Both') ||
        this.userDetails()?.timeCaptureScheme === 'TimeTicking'
      ) {
        this.iconToggled = false;
        this.isTimer = false;
      }
    }
    if (this.userDetails()?.timeCaptureScheme === 'TimeTicking') {
      this.iconToggled = true;
      this.isTimer = true;
      this.entryForm.get('timeSpent')?.disable();
      this.entryForm.get('timeSpent')?.updateValueAndValidity();
    }
    if (this.userDetails()?.timeCaptureScheme === 'Manual') {
      this.iconToggled = false;
      this.isTimer = false;
      this.entryForm.get('timeSpent')?.enable();
      this.entryForm.get('timeSpent')?.updateValueAndValidity();
    }
    if (localStorage.getItem('validator')) {
      const rawValidator = localStorage.getItem('validator');
      const storedValidator = JSON.parse(rawValidator!);
      this.fieldMeta = { ...this.fieldMeta, ...storedValidator };
      if (
        this.fieldMeta.ticketId.required &&
        this.fieldMeta.startTime.required &&
        this.fieldMeta.endTime.required
      ) {
        this.commonService.updateFormValidators(
          this.entryForm,
          [
            'ticketId',
            'projectId',
            'taskTypeId',
            'startTime',
            'endTime',
            'taskDate',
          ],
          Validators.required,
        );
        if (this.entryForm.get('timeSpent')?.value === '00:00:00') {
          this.entryForm.get('timeSpent')?.setValue(null);
        }
        this.entryForm.get('timeSpent')?.updateValueAndValidity();
      } else if (
        this.fieldMeta.timeSpent.required &&
        this.fieldMeta.ticketId.required
      ) {
        this.commonService.updateFormValidators(
          this.entryForm,
          ['ticketId', 'projectId', 'taskTypeId', 'timeSpent', 'taskDate'],
          Validators.required,
        );
        if (this.entryForm.get('timeSpent')?.value === '00:00:00') {
          this.entryForm.get('timeSpent')?.setValue(null);
        }
        // this.entryForm.get('startTime')?.disable();
        // this.entryForm.get('endTime')?.disable();
        this.entryForm.get('timeSpent')?.updateValueAndValidity();
      } else if (
        this.fieldMeta.startTime.required &&
        this.fieldMeta.endTime.required
      ) {
        this.commonService.updateFormValidators(
          this.entryForm,
          ['projectId', 'taskTypeId', 'startTime', 'endTime', 'taskDate'],
          Validators.required,
        );
      } else if (this.fieldMeta.timeSpent.required) {
        // this.entryForm.get('startTime')?.disable();
        // this.entryForm.get('endTime')?.disable();
        this.commonService.updateFormValidators(
          this.entryForm,
          ['projectId', 'taskTypeId', 'timeSpent', 'taskDate'],
          Validators.required,
        );
      }
      if (this.fieldMeta.comment.required) {
        this.commonService.updateFormValidators(
          this.entryForm,
          ['comment'],
          Validators.required,
        );
      }
      if (
        (this.fieldMeta.startTime.required &&
          this.fieldMeta.endTime.required &&
          !this.fieldMeta.timeSpent.required) ||
        this.isTimer
      ) {
        this.entryForm.get('timeSpent')?.disable();
        this.entryForm.get('timeSpent')?.setValue('00:00:00');
      }
    }
    const saved = localStorage.getItem('activeTimer');
    if (saved) {
      const parsed = JSON.parse(saved);
      if (parsed.isStart && !parsed.trackingStopped) {
        // Restore the active-time accumulator so resume reflects banked active
        // seconds, not a raw wall-clock recompute.
        this.accumulatedSeconds = parsed.accumulatedSeconds ?? 0;
        this.segmentStart = parsed.segmentStartTime
          ? new Date(parsed.segmentStartTime)
          : undefined;
        this.isStart = true;
        this.playIcon = PrimeIcons.STOP;
        if (!parsed?.popupOpen) {
          this.onStart();
        }
        this.pTooltipClockIcon = 'Stop Timer';
        this.severityClockIcon = 'danger';
      }
    }
    if (localStorage.getItem('timeEntry')) {
      const rawEntry = localStorage.getItem('timeEntry');
      const entry = JSON.parse(rawEntry!) as TimeEntry;
      const patchData = {
        ...entry,
        taskDate: entry.taskDate ? new Date(entry.taskDate) : null,
        startTime: this.toValidDate(entry?.startTime),
        endTime: this.toValidDate(entry?.endTime),
      };

      if (
        this.playIcon !== PrimeIcons.STOP &&
        !this.fieldMeta.timeSpent.required
      ) {
        patchData.timeSpent = entry.timeSpent || '';
      }
      const taskavl = this.userMetadata()?.roleTaskMappingResponses.find(
        (t) => t.taskTypeId === entry.taskTypeId,
      );

      patchData.taskTypeId = taskavl?.taskTypeId ?? null;

      this.entryForm.patchValue(patchData);

      const isMeetingTask = this.userMetadata()?.roleTaskMappingResponses.find(
        (task) => task.taskTypeId === this.entryForm.get('taskTypeId')?.value,
      );
      const isMeeting = isMeetingTask?.taskTypeName
        ?.toLowerCase()
        .includes('meeting');
      if (isMeeting && this.entryForm.get('taskDate')?.value) {
        if (this.entryForm.get('taskDate')?.value) {
          const startDateFormatted = this.commonService['dateService'].format(
            this.entryForm.get('taskDate')?.value,
            'YYYY-MM-DD',
          );
          this.comments = await this.trackService.getEvents({
            startDate: startDateFormatted,
            endDate: startDateFormatted,
          });
        }
      }
      const selectedTask = this.userMetadata()?.roleTaskMappingResponses.find(
        (t) => t.taskTypeId === this.entryForm.get('taskTypeId')?.value,
      );
      const leaveTypes = [
        ...Object.values(LeaveTypeEnum),
        ...Object.values(AttendanceAdjustmentTypeEnum),
      ].map((type) => type.toLowerCase().trim());

      const isLeaveTask = selectedTask?.taskTypeName
        ? leaveTypes.includes(selectedTask.taskTypeName.toLowerCase().trim())
        : false;
      const isTaskAvailable = patchData.taskTypeId ? true : false;
      this.onProjectChange(
        patchData.projectId || 0,
        false,
        null,
        isTaskAvailable,
      );
      this.checkEarlyRelease(selectedTask);

      if (this.playIcon === PrimeIcons.PLAY && this.isTimer) {
        this.manualEntryTime = {
          start: entry.startTime ? new Date(entry.startTime) : null,
          end: entry.endTime ? new Date(entry.endTime) : null,
          timeSpent: entry.timeSpent ?? null,
        };
        if (!isLeaveTask) {
          this.entryForm.get('timeSpent')?.setValue('00:00:00');
        }
      }
      if (
        isLeaveTask &&
        !Object.values(this.attendanceAdjustmentTypeEnum).includes(
          selectedTask?.taskTypeName as AttendanceAdjustmentTypeEnum,
        )
      ) {
        this.entryForm.get('startTime')?.disable();
        this.entryForm.get('endTime')?.disable();
        this.entryForm.get('timeSpent')?.disable();
        this.entryForm.get('ticketId')?.disable();
        this.playIcon = PrimeIcons.SAVE;
        this.pTooltipClockIcon = 'Save Entry';
      }
    }
    this.entryForm.valueChanges.subscribe((res) => {
      const dataToStore = { ...res };
      dataToStore.timeSpent = this.entryForm.get('timeSpent')?.value;
      if (!this.isTimer) {
        const taskType = this.userMetadata()?.roleTaskMappingResponses.find(
          (x) => x.taskTypeId === dataToStore.taskTypeId,
        );
        const timeSpent = taskType?.isTimingRequired
          ? this.entryForm.get('startTime')?.value &&
            this.entryForm.get('endTime')?.value
            ? this.calculateTimeSpent(
                this.entryForm.get('startTime')?.value,
                this.entryForm.get('endTime')?.value,
              )
            : '00:00:00'
          : this.entryForm.get('timeSpent')?.value;
        this.manualEntryTime = {
          start: this.entryForm.get('startTime')?.value,
          end: this.entryForm.get('endTime')?.value,
          timeSpent: timeSpent,
        };
      }
      localStorage.setItem('timeEntry', JSON.stringify(dataToStore));
    });
    await this.loadTabData(this.selectedTab, true);
    await this.updateProgress();
  }
  get progressFillStyle() {
    return {
      backgroundColor: this.progressColor,
    };
  }
  getOverTimeHour(): number {
    const hour = Number(this.userDetails()?.minHours?.split(':')[0] || 0);
    return hour + 2;
  }
  isAnyFilterApplied(): boolean {
    const hasFilterValues =
      !!this.table?.filters &&
      Object.values(this.table.filters).some((f: any) =>
        Array.isArray(f)
          ? f.some((filter) => filter.value != null && filter.value !== '')
          : f.value != null && f.value !== '',
      );
    const isTabWeekWithTaskDate = this.isTimer
      ? null
      : this.tabData[this.selectedTab].id === 'week' &&
        !!this.entryForm.get('taskDate')?.value;
    const hasGlobalFilter = !!this.searchText?.trim();

    return hasFilterValues || isTabWeekWithTaskDate || hasGlobalFilter;
  }

  updateProgress() {
    const parts = this.totalTimeSpent.split(':').map(Number);
    let hh = 0,
      mm = 0,
      ss = 0;
    if (parts.length === 2) [hh, mm] = parts;
    else if (parts.length === 3) [hh, mm, ss] = parts;
    const totalHours = hh + mm / 60 + ss / 3600;
    let overtimeHours = this.getOverTimeHour();
    const [minHours, minMinutes] = this.userService
      .userDetails()
      ?.minHours.split(':') || ['0', '0'];
    const hour = parseInt(minHours, 10);
    const minut = parseInt(minMinutes, 10);
    const minNumericValue = hour + minut / 60;
    let threshold = minNumericValue;
    this.minHoursStr = this.userDetails()?.minHours || '08:30:00';
    this.minHours = this.timeStringToDecimalHours(this.minHoursStr);
    if (this.selectedTab === 2 && this.thisWeekData) {
      this.minHours = this.minHours * 5;
      this.minHoursStr = this.commonService.hoursToTimeString(this.minHours);
      threshold = 37.5;
      const range = this.entryForm.value.rangeDates;
      if (range && Array.isArray(range) && range[0] && range[1]) {
        const start = new Date(range[0]);
        const end = new Date(range[1]);
        if (start.toDateString() === end.toDateString()) {
          threshold = this.minHours;
        }
      }
      overtimeHours = this.getOverTimeHour() * 5;
    }

    this.progressValue = Math.min((totalHours / threshold) * 100, 100);
    const taskDate = new Date(
      this.entryForm.get('taskDate')?.value,
    ).toDateString();
    const dateType = this.highlightedDates.find(
      (x) => new Date(x.date).toDateString() === taskDate,
    );

    if (dateType?.class === 'leaveColor') {
      this.progressColor = '#e9c870';
    } else if (dateType?.class === 'holidayColor') {
      this.progressColor = '#c998f6';
    } else if (totalHours < threshold) {
      this.progressColor = '#f2adad';
    } else if (totalHours >= threshold && totalHours <= overtimeHours) {
      this.progressColor = '#65cc7d';
    } else if (totalHours > overtimeHours) {
      this.progressColor = '#74a7e8';
    } else {
      this.progressColor = '';
    }

    const totalSeconds = hh * 3600 + mm * 60 + ss;
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    this.timeDisplay = `${hours} hours ${minutes} minutes`;
  }

  checkTooltipVisibility(el: HTMLElement) {
    this.shouldShowTooltip = el.scrollWidth > el.clientWidth;
  }

  async onMonthChanged(event: any) {
    this.dateService.getDate().format('YYYY-MM-DD');
    this.colorCode();
  }

  getMonthStartEnd(
    year: number,
    month: number,
  ): { startDate: Date; endDate: Date } {
    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth() + 1;

    const startDate = new Date(year, month - 1, 1);
    let endDate: Date;

    if (year === currentYear && month === currentMonth) {
      endDate = today;
    } else {
      endDate = new Date(year, month, 0);
    }

    return { startDate, endDate };
  }
  parseLocalDate(dateString: string): Date {
    let year: number, month: number, day: number;
    if (dateString.includes('-')) {
      [year, month, day] = dateString.split('-').map(Number);
    } else {
      [month, day, year] = dateString.split('/').map(Number);
    }
    return new Date(year, month - 1, day); // year, monthIndex(0-based), day
  }

  async colorCode() {
    const today = new Date();
    const startDate = new Date(today);
    startDate.setMonth(startDate.getMonth() - 6);
    const endDate = today;
    if (
      this.commonService.isFutureMonth(
        endDate.getFullYear(),
        endDate.getMonth() + 1,
      )
    ) {
      return;
    }
    const startDateFormatted = this.commonService['dateService'].format(
      startDate,
      'YYYY-MM-DD',
    );
    const endDateFormatted = this.commonService['dateService'].format(
      endDate,
      'YYYY-MM-DD',
    );

    if (
      !this.commonService.checkDateRangeExists(
        this.highlightedDates,
        startDate,
        endDate,
      )
    ) {
      const response = await this.reportService.getMyAdherenceReport(
        startDateFormatted,
        endDateFormatted,
      );
      const highlighted: { date: Date; class: string }[] = [];
      this.myAdherenceReport = response;

      Object.entries(response.timeByDate as Record<string, string>).forEach(
        ([dateString, value]) => {
          const date = this.parseLocalDate(dateString);
          let cssClass: string | null = '';
          if (value.includes('/ ')) {
            const hours = value.split('/ ')[1];
            const halfDayLeaveTypes = Object.values(HalfDayLeaveTypeEnum);
            const isLeaveTasks = value.split('/ ')[0].trim();
            const isHalfDayLeave =
              isLeaveTasks &&
              halfDayLeaveTypes.includes(isLeaveTasks as HalfDayLeaveTypeEnum);
            if (isHalfDayLeave) {
              value = this.addMultipleTimes([
                this.commonService.divideTime(
                  this.userService.userDetails()?.minHours || '00:00:00',
                ),
                hours,
              ]);
            } else {
              value = this.addMultipleTimes([
                this.userService.userDetails()?.minHours || '00:00:00',
                hours,
              ]);
            }
          }
          if (value === 'Weekend') {
            cssClass = 'weekendColor';
          } else if (
            value === 'Leave' ||
            value === 'PTO' ||
            value === 'Unpaid Leave'
          ) {
            cssClass = 'leaveColor';
          } else if (value === 'Entry Missing') {
            cssClass = 'missingEntryColor';
          } else if (value === 'Holiday') {
            cssClass = 'holidayColor';
          } else {
            const [hoursStr, minutesStr] = value.split(':');
            const [minHours, minMinutes] = this.userService
              .userDetails()
              ?.minHours.split(':') || ['0', '0'];

            const hours = parseInt(hoursStr, 10);
            const minutes = parseInt(minutesStr, 10);
            const numericValue = hours + minutes / 60;

            const hour = parseInt(minHours, 10);
            const minut = parseInt(minMinutes, 10);
            const minNumericValue = hour + minut / 60;

            if (numericValue < minNumericValue) {
              cssClass = 'missingEntryColor';
            } else if (
              numericValue >= minNumericValue &&
              numericValue <= this.getOverTimeHour()
            ) {
              cssClass = 'entryDoneColor ';
            } else if (numericValue > this.getOverTimeHour()) {
              cssClass = 'overtimeColor ';
            } else {
              cssClass = '';
            }
          }

          highlighted.push({ date, class: cssClass });
        },
      );

      this.highlightedDates = highlighted;
    }
  }

  getHighlightClass(dateObj: any): string | null {
    return (
      this.highlightedDates.find(
        (hd) =>
          hd.date.getDate() === dateObj.day &&
          hd.date.getMonth() === dateObj.month &&
          hd.date.getFullYear() === dateObj.year,
      )?.class || null
    );
  }

  isTimeOnlyFormat(value: string): boolean {
    return /^\d{2}:\d{2}(:\d{2})?$/.test(value);
  }

  toValidDate(value: string | null | undefined): Date | null {
    if (!value) return null;

    if (this.isTimeOnlyFormat(value)) {
      const [h, m, s] = value.split(':').map(Number);
      const date = new Date();
      date.setHours(h, m, s || 0, 0);
      return date;
    }

    const parsed = new Date(value);
    return isNaN(parsed.getTime()) ? null : parsed;
  }

  filterComments(event: AutoCompleteCompleteEvent) {
    this.filteredComments = this.comments
      .filter((p) =>
        p.subject.toLowerCase().includes(event.query.toLowerCase()),
      )
      .sort((a, b) => {
        const query = event.query.toLowerCase();
        const aValueStart = a.subject.toLowerCase().startsWith(query);
        const bValueStart = b.subject.toLowerCase().startsWith(query);

        if (aValueStart && !bValueStart) {
          return -1;
        } else if (!aValueStart && bValueStart) {
          return 1;
        } else {
          return 0;
        }
      });
  }

  patchMeetingComment(event: AutoCompleteSelectEvent, isEdit: boolean) {
    const meeting = event.value as UserEvent;
    if (!isEdit) {
      if (this.isTimer) {
        this.entryForm.patchValue({
          comment: meeting.subject,
        });
      } else {
        this.entryForm.patchValue({
          comment: meeting.subject,
          startTime: this.commonService.parseUtcTimeToLocal(
            meeting.startTime,
            meeting.taskDate,
          ),
          endTime: this.commonService.parseUtcTimeToLocal(
            meeting.endTime,
            meeting.taskDate,
          ),
          timeSpent: meeting.timeSpent,
        });
      }
    } else {
      this.delayPopup();
    }
  }

  parseTimeStringToDate(timeStr: string, baseDate: string | Date): Date {
    const [hours, minutes, seconds] = timeStr.split(':').map(Number);

    const date = new Date(typeof baseDate === 'string' ? baseDate : baseDate);

    date.setHours(hours, minutes, seconds || 0, 0);
    return date;
  }

  async loadTrackTask(startDate: string, endDate: string) {
    const data = await this.trackerService.getTrack(startDate, endDate);
    this.totalTimeSpent = data.totalTimeSpent!;
    const date =
      data.timeEntries[0]?.taskDate != null
        ? this.parseLocalDate(data.timeEntries[0].taskDate)
        : null;
    const taskDate = this.entryForm.get('taskDate')?.value;
    const manualStart = this.manualEntryTime?.start;

    const formattedTaskDate = this.commonService['dateService'].format(
      new Date(taskDate),
      'YYYY/MM/DD',
    );
    const formattedManualStart = this.commonService['dateService'].format(
      new Date(manualStart!),
      'YYYY/MM/DD',
    );
    const leaveTypes = [
      ...Object.values(LeaveTypeEnum),
      ...Object.values(AttendanceAdjustmentTypeEnum),
    ]
      .map((type) => type.toLowerCase().trim())
      .filter((type) => type !== 'pto');
    const selectedTask = this.userMetadata()?.roleTaskMappingResponses.find(
      (t) => t.taskTypeId === this.entryForm.get('taskTypeId')?.value,
    );

    const isLeaveTask = selectedTask?.taskTypeName
      ? leaveTypes.includes(selectedTask.taskTypeName.toLowerCase().trim()) ||
        selectedTask?.taskTypeName === 'PTO'
      : false;
    if (date && data.timeEntries[0]?.endTime && !this.isTimer && !isLeaveTask) {
      const time = data.timeEntries.filter(
        (e) =>
          !(
            (e.startTime ?? '00:00:00') === '00:00:00' &&
            (e.endTime ?? '00:00:00') === '00:00:00'
          ),
      )[0]?.endTime;
      if (time) {
        const [hours, minutes, seconds] = time!.split(':').map(Number);
        date.setHours(hours, minutes, seconds || 0, 0);
        this.entryForm.get('startTime')?.patchValue(date);
        if (this.entryForm.get('endTime')?.value !== null) {
          this.calculateTimeDifference(
            this.entryForm.get('startTime')?.value,
            this.entryForm.get('endTime')?.value,
            false,
            null,
          );
        }
      } else {
        this.entryForm.get('startTime')?.patchValue(null);
      }
    } else if (
      manualStart &&
      !this.isTimer &&
      formattedTaskDate !== formattedManualStart
    ) {
      this.entryForm.get('startTime')?.patchValue(null);
      this.calculateTimeDifference(
        this.entryForm.get('startTime')?.value,
        this.entryForm.get('endTime')?.value,
        false,
        null,
      );
    }
    this.highlightedDates = [];
    await this.colorCode();
    await this.updateProgress();
    return data.timeEntries!.map((item: any) => ({
      ...item,
      startTimeEndTime: [item.startTime, item.endTime].join(' - '),
    }));
  }
  // checkPrevWorkingDayEntry(taskDate: string, isLeaveTask: boolean): boolean {
  //   const userDetails = this.userService.userDetails();
  //   const minHours = userDetails?.minHours ?? '00:00:00';

  //   const prevDate = this.parseLocalDate(taskDate);
  //   prevDate.setDate(prevDate.getDate() - 1);

  //   while ([0, 6].includes(prevDate.getDay())) {
  //     prevDate.setDate(prevDate.getDate() - 1);
  //   }
  //   const formattedPrevDate = this.commonService['dateService'].format(
  //     prevDate,
  //     'YYYY-MM-DD',
  //   );
  //   const valueRaw =
  //     this.myAdherenceReport?.timeByDate?.[formattedPrevDate] ?? null;

  //   const value = this.processLeaveValue(valueRaw, minHours);

  //   const prevDayMinutes = this.timeToMinutes(value || '');
  //   const minHourMinutes = this.timeToMinutes(minHours);

  //   const prevDateDisplay = this.commonService['dateService'].format(
  //     prevDate,
  //     'DD/MM/YYYY',
  //   );
  //   const taskDateDisplay = this.commonService['dateService'].format(
  //     taskDate,
  //     'DD/MM/YYYY',
  //   );

  //   const showMissedDayError = () => {
  //     this.commonService.showToast(
  //       'Error',
  //       `Please complete entries for ${prevDateDisplay} (Missed day) before updating entries for ${taskDateDisplay} (Current day).`,
  //     );
  //   };

  //   if (prevDayMinutes < minHourMinutes) {
  //     showMissedDayError();
  //     return false;
  //   }

  //   if (valueRaw === 'Entry Missing' && !isLeaveTask) {
  //     showMissedDayError();
  //     return false;
  //   }

  //   return true;
  // }
  private processLeaveValue(
    value: string | null,
    minHours: string,
  ): string | null {
    if (!value) return value;

    const lower = value.toLowerCase();
    const fullDayLeaveTypes = Object.values(FullDayLeaveTypeEnum);

    if (lower.includes('half day')) {
      const halfDayHours = value.split('/')?.[1] ?? '00:00:00';
      const dividedTime = this.commonService.divideTime(minHours);
      return this.addMultipleTimes([dividedTime, halfDayHours]);
    }

    if (fullDayLeaveTypes.includes(value as FullDayLeaveTypeEnum)) {
      return minHours;
    }

    return value;
  }
  timeToMinutes(time: string): number {
    const [h, m, s] = time.split(':').map(Number);
    return h * 60 + m + (s || 0) / 60;
  }
  async onSave() {
    const selectedTask = this.userMetadata()?.roleTaskMappingResponses.find(
      (t) => t.taskTypeId === this.entryForm.get('taskTypeId')?.value,
    );
    const leaveTypes = [
      ...Object.values(LeaveTypeEnum),
      ...Object.values(AttendanceAdjustmentTypeEnum),
    ]
      .map((type) => type.toLowerCase().trim())
      .filter((type) => type !== 'pto');

    const isLeaveTask = selectedTask?.taskTypeName
      ? leaveTypes.includes(selectedTask.taskTypeName.toLowerCase().trim()) ||
        selectedTask?.taskTypeName === 'PTO'
      : false;
    const formData = this.entryForm.getRawValue();
    const { taskDate, startTime, endTime, ...rest } = formData;

    const today = this.commonService['dateService'].format(
      new Date(taskDate.setDate(taskDate.getDate())),
      'YYYY-MM-DD',
    );
    const actualToday = this.commonService['dateService'].format(
      new Date(),
      'YYYY-MM-DD',
    );
    const isPastDate = today !== actualToday;
    // if (!this.checkPrevWorkingDayEntry(today, isLeaveTask)) {
    //   return false;
    // }
    const isBasicFormValid = this.commonService.validateForm(this.entryForm);
    if (!isBasicFormValid) {
      this.commonService.showToast('Error', 'Please fill all required fields!');
      return false;
    }
    const sTime = this.entryForm.get('startTime')?.value;
    const eTime = this.entryForm.get('endTime')?.value;
    if (
      (sTime && !eTime) ||
      (!sTime && eTime) ||
      (sTime && eTime && sTime === eTime)
    ) {
      this.commonService.showToast(
        'Error',
        'Start and end time are optional for the selected task, but if you enter one, the other is required.',
      );
      return false;
    }

    const timeSpentZeroError = this.getZeroTimeError(
      this.entryForm.get('timeSpent')?.value,
    );
    const isFormInvalid = !this.commonService.validateForm(this.entryForm);

    const errorMsg = [
      isFormInvalid ? 'Please fill all the required fields! updated' : '',
      !selectedTask?.isTimingRequired ? timeSpentZeroError || '' : '',
    ]
      .filter(Boolean)
      .join('\n');

    if (errorMsg) {
      this.commonService.showToast('Error', errorMsg);
      return false;
    }

    let stime = startTime;
    let etime = endTime;

    if (
      (startTime !== null && startTime !== '') ||
      (endTime !== null && endTime !== '')
    ) {
      stime = this.dateService.format(new Date(startTime), 'HH:mm:ss');

      etime = this.dateService.format(new Date(endTime), 'HH:mm:ss');
    }
    const newEntry = {
      ...rest,
      taskDate: today,
      startTime: stime,
      endTime: etime,
      checked: undefined,
      // Tell the API whether this entry was timer-captured. The server only acts on
      // this for the "Both" scheme (timer vs manual); TimeTicking/Manual are derived
      // from the scheme regardless.
      isTimeTicking:
        this.isTimer || this.userDetails()?.timeCaptureScheme === 'TimeTicking',
      isPastDate,
    };
    try {
      let totalTimeSpent = '';
      if (this.isTimer) {
        totalTimeSpent = this.addMultipleTimes([
          newEntry.timeSpent,
          this.timeTickingTimeSpent,
        ]);
      } else {
        if (this.selectedTab === 2) {
          const totalTimeSpentByDate = this.trackEntryTable
            .filter(
              (x) =>
                x.taskDate ===
                this.dateService.format(
                  new Date(newEntry.taskDate),
                  'M/D/YYYY',
                ),
            )
            .map((item) => item?.timeSpent);
          totalTimeSpentByDate.push(newEntry.timeSpent);
          totalTimeSpent = this.addMultipleTimes(totalTimeSpentByDate);
        } else {
          totalTimeSpent = this.addMultipleTimes([
            newEntry.timeSpent,
            this.totalTimeSpent,
          ]);
        }
      }

      this.updateProgress();
      if (this.getTimePopupLevel(totalTimeSpent) === 'Over 16 hours') {
        await this.confirmWithDialog(
          'You cannot log more than 16 hours in a single day!',
          false,
        );
        if (this.isTimer) {
          this.entryForm.get('timeSpent')?.setValue('00:00:00');
        }
        return true;
      } else if (this.getTimePopupLevel(totalTimeSpent) === 'Over 13 hours') {
        const confirmed = await this.confirmWithDialog(
          "Please double-check your entry to ensure it's accurate. If this is correct, choose <b><u>YES</u></b>. Otherwise, choose <b><u>NO</u></b> and entered data will be saved.",
          true,
        );
        if (!confirmed) {
          return false;
        }
      } else if (
        this.getTimePopupLevel(totalTimeSpent) === 'Overtime' &&
        !isLeaveTask
      ) {
        const confirmed = await this.confirmWithDialog(
          "<strong> Heads up!</strong> You've entered more than <strong>" +
            this.userDetails()?.minHours +
            ' hours</strong>. If this includes overtime, please provide a proper comment do you want to proceed.',
          true,
        );
        if (!confirmed) {
          return false;
        }
      }

      await this.trackerService.createTrack(newEntry);
      if (isLeaveTask) {
        this.clearEntry(true);
      } else {
        this.clearEntry();
      }
      const currentDate = new Date();
      if (taskDate > currentDate) {
        this.thisWeekData = true;
      }
      if (
        (this.userDetails()?.timeCaptureScheme !== 'Manual' &&
          this.entryBtnIcon === PrimeIcons.PENCIL) ||
        this.isTimer
      ) {
        this.entryForm.patchValue({ timeSpent: '00:00:00' });
      } else {
        this.entryForm.patchValue({ timeSpent: '' });
      }
      this.entryForm.updateValueAndValidity();
      this.isLeaveProject(false, false);
      this.commonService.showToast('Success', 'Time entry saved successfully!');
      this.highlightedDates = [];

      await this.loadTabData(this.selectedTab);
      this.manualEntryTime = { start: null, end: null, timeSpent: null };
      this.focusInput();
      return true;
    } catch (error: any) {
      const errors = error?.error?.errors;
      if (Array.isArray(errors)) {
        errors.forEach((element: any) => {
          this.commonService.showToast('Error', element);
        });
      }
      return false;
    }
  }

  async onDeleteTrack(trackIds: number[]) {
    try {
      await this.trackerService.deleteTrack(trackIds);
      await this.loadTabData(this.selectedTab);
      this.highlightedDates = [];
      this.commonService.showToast(
        'Success',
        'Time entry deleted successfully!',
      );
      this.selectedTrack = [];
    } catch (error: any) {
      const errors = error?.error?.errors;
      if (Array.isArray(errors)) {
        errors.forEach((element: any) => {
          this.commonService.showToast('Error', element);
        });
      }
    }
  }
  onClear(dt?: any) {
    this.searchText = '';
    if (dt) {
      dt.clear();
    }
    if (
      this.tabData[this.selectedTab].id === 'week' &&
      this.entryForm.get('taskDate')?.value
    ) {
      if (!this.isTimer) {
        this.entryForm.get('taskDate')?.setValue(null);
      }
      this.thisWeekData = true;
      this.loadTabData(this.selectedTab);
    }
  }

  onTabChange() {
    this.totalTimeSpent = '00:00:00';
    this.updateProgress();
    if (this.selectedTab === 2) {
      this.thisWeekData = true;
    }
    this.selectedTrack = [];
    const isTimerTab =
      (this.selectedTab === 1 || this.selectedTab === 2) &&
      this.isTimer &&
      !this.isStart;
    const selectedTask = this.userMetadata()?.roleTaskMappingResponses.find(
      (t) => t.taskTypeId === this.entryForm.get('taskTypeId')?.value,
    );

    const isManualTab =
      this.selectedTab === 0 &&
      !this.isTimer &&
      (this.userDetails()?.timeCaptureScheme === 'TimeTicking' ||
        this.userDetails()?.timeCaptureScheme === 'Both') &&
      Object.values(this.attendanceAdjustmentTypeEnum).includes(
        selectedTask?.taskTypeName as AttendanceAdjustmentTypeEnum,
      );
    if (isTimerTab || isManualTab) {
      this.entryOption();
    }

    // Suppress the loadTabData auto-toggle only when we already toggled the mode
    // above. Otherwise (e.g. returning to Today with no attendance task selected)
    // let the TimeTicking restore fallback in loadTabData re-enable timer mode.
    this.loadTabData(this.selectedTab, isTimerTab || isManualTab);
  }

  loadTabData(index: number, isIntilialLoad = false) {
    const tabId = this.tabData[index].id;
    this.fetchDataForTab(tabId);
    if (
      this.userDetails()?.timeCaptureScheme === 'TimeTicking' &&
      !this.isTimer &&
      tabId === 'today' &&
      !isIntilialLoad
    ) {
      this.entryOption();
    }
  }

  fetchDataForTab(tabId: string): Promise<any[]> {
    const now = new Date();
    const todayStart = new Date(now);
    switch (tabId) {
      case 'today': {
        const today = this.commonService['dateService'].format(
          new Date(todayStart.setDate(todayStart.getDate())),
          'YYYY/MM/DD',
        );
        this.entryForm.patchValue({ taskDate: new Date(today) });
        return this.loadTrackTask(today, today).then((res) => {
          this.trackEntryTable = res;
          this.timeTickingTimeSpent = this.totalTimeSpent;

          return res;
        });
      }
      case 'prevWorkingDay': {
        const today = new Date();
        const prev = new Date(today);

        do {
          prev.setDate(prev.getDate() - 1);
        } while (prev.getDay() === 0 || prev.getDay() === 6);

        const formatted = this.commonService['dateService'].format(
          prev,
          'YYYY/MM/DD',
        );
        if (!this.isTimer) {
          this.entryForm.patchValue({ taskDate: prev });
        }

        return this.loadTrackTask(formatted, formatted).then((res) => {
          this.trackEntryTable = res;
          return res;
        });
      }
      case 'week': {
        const todayStart = new Date();
        const todayEnd = new Date();

        const startDate = this.commonService['dateService'].format(
          this.thisWeekData
            ? new Date(todayStart.setDate(todayStart.getDate() - 7))
            : this.entryForm.get('taskDate')?.value,
          'YYYY/MM/DD',
        );
        if (this.thisWeekData && !this.isTimer) {
          this.entryForm.get('taskDate')?.setValue(null);
        }
        const endDate = this.commonService['dateService'].format(
          this.thisWeekData
            ? new Date(todayEnd)
            : this.entryForm.get('taskDate')?.value,
          'YYYY/MM/DD',
        );
        return this.loadTrackTask(startDate, endDate).then((res) => {
          this.trackEntryTable = res;
          return res;
        });
      }
      default:
        return Promise.resolve([]);
    }
  }

  entryOption() {
    const selectedTask = this.userMetadata()?.roleTaskMappingResponses.find(
      (t) => t.taskTypeId === this.entryForm.get('taskTypeId')?.value,
    );
    const leaveTypes = [
      ...Object.values(LeaveTypeEnum),
      ...Object.values(AttendanceAdjustmentTypeEnum),
    ].map((type) => type.toLowerCase().trim());

    const isLeaveTask = selectedTask?.taskTypeName
      ? leaveTypes.includes(selectedTask.taskTypeName.toLowerCase().trim())
      : false;
    this.iconToggled = !this.iconToggled;
    this.isTimer = !this.isTimer;
    this.entryBtnIcon = this.isTimer ? PrimeIcons.PENCIL : PrimeIcons.STOPWATCH;
    if (!this.isTimer && !this.isStart && !isLeaveTask) {
      let timeSpent;
      if (
        this.entryForm.get('startTime')?.value !== null &&
        this.entryForm.get('endTime')?.value !== null
      ) {
        timeSpent =
          (this.manualEntryTime.timeSpent === '00:00:00' &&
            this.entryForm.get('startTime')?.value &&
            this.entryForm.get('endTime')?.value) ||
          selectedTask?.isTimingRequired
            ? this.calculateTimeSpent(
                this.entryForm.get('startTime')?.value,
                this.entryForm.get('endTime')?.value,
              )
            : this.manualEntryTime.timeSpent;
      }
      this.entryForm.patchValue({
        startTime: this.manualEntryTime.start,
        endTime: this.manualEntryTime.end,
        timeSpent: timeSpent,
      });
    }
    localStorage.setItem('entryType', this.entryBtnIcon);
    const taskDate: Date = new Date(this.entryForm.value.taskDate);
    if (!this.isTimer && this.isStart) {
      if (!isLeaveTask) {
        const now = new Date();
        const start = new Date(this.entryForm.get('startTime')?.value);
        const end = new Date(now);
        const timeSpent = this.calculateTimeSpent(start, end);
        this.isStart = false;
        this.isValidtor = true;
        this.playIcon = PrimeIcons.PLAY;
        this.pTooltipClockIcon = 'Start Timer';
        this.timerSubscription?.unsubscribe();
        this.onStart();
        this.entryForm.patchValue({
          taskDate: taskDate,
          startTime: start,
          endTime: end,
          timeSpent: timeSpent,
        });
      } else {
        this.entryForm.get('timeSpent')?.disable();
      }
      this.timerSubscription?.unsubscribe();
      this.entryForm.get('startTime')?.updateValueAndValidity();
      this.entryForm.get('endTime')?.updateValueAndValidity();
      this.entryForm.get('timeSpent')?.updateValueAndValidity();
      this.entryForm.get('taskDate')?.updateValueAndValidity();
    } else {
      this.severityClockIcon = null;
      const taskDate = new Date(this.entryForm.value.taskDate);
      this.entryForm.patchValue({
        taskDate,
      });
      this.entryForm.updateValueAndValidity();
      if (this.entryForm.get('timeSpent')?.value === '00:00:00') {
        this.entryForm.get('timeSpent')?.setValue(null);
        this.entryForm.updateValueAndValidity();
      }
    }
    if (!selectedTask?.isTimingRequired && !this.isTimer && !isLeaveTask) {
      this.entryForm.get('timeSpent')?.enable();
      // if (this.entryForm.get('taskTypeId')?.value) {
      //   this.entryForm.get('startTime')?.disable();
      //   this.entryForm.get('endTime')?.disable();
      // }
    } else {
      this.entryForm.get('timeSpent')?.disable();
      // if (
      //   (!this.entryForm.get('startTime')?.value ||
      //     !this.entryForm.get('endTime')?.value ||
      //     !this.entryForm.get('timeSpent')?.value) &&
      //   !isLeaveTask
      // ) {
      //   this.entryForm.patchValue({ timeSpent: '00:00:00' });
      //   this.entryForm.updateValueAndValidity();
      // }
      if (this.entryForm.get('taskTypeId')?.value) {
        this.entryForm.get('timeSpent')?.setValidators([Validators.required]);
        this.entryForm.get('timeSpent')?.updateValueAndValidity();
      }
    }
    if (selectedTask?.isTimingRequired && !this.isTimer) {
      this.fieldMeta.timeSpent.required = false;
    } else {
      this.fieldMeta.timeSpent.required = true;
    }
    if (this.isTimer && !this.isStart && !isLeaveTask) {
      this.entryForm.get('timeSpent')?.setValue('00:00:00');
    }
  }

  async timer(): Promise<void> {
    if (this.playIcon === PrimeIcons.SAVE) {
      this.onSave();
      //  this.clearEntry(false, true);
      return;
    }
    const now = new Date();
    now.setMilliseconds(0);
    if (this.isStart) {
      await new Promise((resolve) => setTimeout(resolve));
      const form = this.entryForm;
      let isValid = true;

      const end = new Date(now);
      // Tracked time is the idle-excluded active total, read from the SHARED
      // state so this works even if another tab owns the timer.
      const activeSeconds = this.sharedActiveSeconds(end);
      const timeSpent = this.formatTime(activeSeconds);
      const start = new Date(end.getTime() - activeSeconds * 1000);
      this.entryForm.get('startTime')?.setValue(start);
      this.entryForm.patchValue({
        timeSpent,
        endTime: now,
      });
      Object.keys(form.controls).forEach((key) => {
        const control = form.get(key);
        control?.markAsTouched();
        control?.updateValueAndValidity();
        control?.markAsDirty();
        if (control?.invalid) {
          isValid = false;
        }
      });
      if (isValid) {
        this.entryForm.updateValueAndValidity();
        this.isValidtor = true;
        // First tab to clear activeTimer wins the save; if another tab already
        // stopped this session, abort without saving a duplicate entry.
        if (!this.claimStop()) {
          this.handleExternalStop();
          return;
        }
        this.timerSubscription?.unsubscribe();
        const isSaved = await this.onSave();
        if (!isSaved) {
          // Save failed: restore the session (we already cleared activeTimer via
          // claimStop) so the user can fix the issue and stop again, resuming
          // from the accumulated total with a fresh segment.
          this.accumulatedSeconds = activeSeconds;
          this.segmentStart = new Date();
          this.isTimerOwner = true;
          this.saveActiveTimer(
            this.accumulatedSeconds,
            this.segmentStart,
            this.segmentStart,
            false,
          );
          this.onStart();
          return;
        }
      } else {
        this.entryForm.patchValue({
          endTime: now,
        });
        this.onSave();
        return;
      }
    } else {
      this.entryForm.patchValue({
        startTime: now,
        endTime: now,
        timeSpent: '00:00:00',
      });
    }

    this.isStart = !this.isStart;
    this.playIcon = this.isStart ? PrimeIcons.STOP : PrimeIcons.PLAY;
    this.pTooltipClockIcon = this.isStart ? 'Stop Timer' : 'Start Timer';
    this.severityClockIcon = this.isStart ? 'danger' : null;
    this.onStart();
    this.switchToManualIfNotToday();
  }

  /** After stopping a running timer while on a non-Today tab, the live timer no
   * longer applies (you can't track wall-clock time for a past day), so flip the
   * UI to the manual (non-time-ticking) entry control. Guarded on !isStart so it
   * only runs once the timer has fully stopped, never mid-run. */
  private switchToManualIfNotToday(): void {
    const scheme = this.userDetails()?.timeCaptureScheme;
    if (
      this.isTimer &&
      !this.isStart &&
      this.tabData[this.selectedTab].id !== 'today' &&
      (scheme === 'TimeTicking' || scheme === 'Both')
    ) {
      this.entryOption();
    }
  }

  getRequiredFieldErrorMessage(
    formGroup: FormGroup,
    fieldLabels: Record<string, string>,
  ): string {
    const missingFields: string[] = [];
    for (const key in fieldLabels) {
      if (formGroup.controls[key]?.invalid) {
        missingFields.push(fieldLabels[key]);
      }
    }

    return missingFields.length
      ? `${missingFields.join(', ')} ${missingFields.length > 1 ? 'are' : 'is'} required.`
      : '';
  }

  calculateTimeSpent(start: Date, end: Date): string {
    const diffMs = end.getTime() - start.getTime();
    const hours = Math.floor(diffMs / 3600000);
    const minutes = Math.floor((diffMs % 3600000) / 60000);
    const seconds = Math.floor((diffMs % 60000) / 1000);

    return [hours, minutes, seconds]
      .map((unit) => unit.toString().padStart(2, '0'))
      .join(':');
  }

  /** Live tracked seconds = banked active seconds + the current open segment. */
  private currentActiveSeconds(now: Date = new Date()): number {
    const segment = this.segmentStart
      ? Math.max(
          0,
          Math.floor((now.getTime() - this.segmentStart.getTime()) / 1000),
        )
      : 0;
    return this.accumulatedSeconds + segment;
  }

  async onStart() {
    if (!this.isStart) {
      this.cleanupTimer();
      return;
    }

    // Guarantee a single live subscription: tear down any prior interval before
    // creating a new one so timers never double-count.
    this.timerSubscription?.unsubscribe();
    this.timerSubscription = undefined;

    const activeTimer = this.getActiveTimer();
    const confirmationData = this.getConfirmationData();
    this.confirmationInProgress = false;

    if (activeTimer) {
      // Resume: restore the banked total and the live segment from storage. This
      // tab only owns the timer if it was the previous owner (e.g. a re-subscribe
      // in the same tab); a different tab resumes as a read-only follower.
      this.accumulatedSeconds = activeTimer.accumulatedSeconds ?? 0;
      this.segmentStart = activeTimer.segmentStartTime
        ? new Date(activeTimer.segmentStartTime)
        : new Date();
      // Own the timer if we were the previous owner, or if the stored heartbeat
      // is already stale (e.g. a reload assigns a new tabId, or the owner tab is
      // gone). A different, still-live owner keeps this tab as a follower.
      this.isTimerOwner =
        activeTimer.ownerId === this.tabId ||
        !activeTimer.ownerHeartbeat ||
        Date.now() - activeTimer.ownerHeartbeat > this.TIMER_OWNER_STALE_MS;
    } else {
      // Fresh start: bank nothing, begin a new segment now, and claim ownership.
      this.accumulatedSeconds = 0;
      this.segmentStart = new Date();
      this.isTimerOwner = true;
      this.saveActiveTimer(0, this.segmentStart, this.segmentStart, false);
    }

    if (confirmationData) {
      // Reload landed mid-dialog: stay frozen at the banked total and resume the
      // confirmation with the remaining time.
      this.accumulatedSeconds =
        confirmationData.frozenSeconds ?? this.accumulatedSeconds;
      this.segmentStart = undefined;

      const confirmStart = new Date(confirmationData.confirmationStartTime);
      const now = new Date();
      const elapsed = (now.getTime() - confirmStart.getTime()) / 1000;
      const remaining = 60 - elapsed;

      if (remaining > 0) {
        this.confirmationInProgress = true;
        const confirmContinue = await this.handleConfirmationDialog(remaining);
        if (!confirmContinue) return;
        this.confirmationInProgress = false;
      } else {
        localStorage.removeItem('confirmationData');
      }
      // Start a fresh segment after confirming so the dialog window is excluded,
      // and persist it so a subsequent reload keeps the banked total. The tab
      // that handled the dialog becomes the owner.
      this.segmentStart = new Date();
      this.isTimerOwner = true;
      this.saveActiveTimer(
        this.accumulatedSeconds,
        this.segmentStart,
        this.segmentStart,
        false,
      );
    }

    this.timerSubscription = timer(0, 1000).subscribe(async () => {
      if (this.confirmationInProgress) return;

      const now = new Date();
      const activeTimer = this.getActiveTimer();

      // Session ended in another tab (Stop/Save removed activeTimer).
      if (!activeTimer) {
        this.handleExternalStop();
        return;
      }

      // Follower tab: render the shared value read-only. Take over only if the
      // owner's heartbeat is stale (its tab was closed) and no confirmation
      // dialog is mid-flight in the owner tab.
      if (!this.isTimerOwner) {
        const confirming = !!this.getConfirmationData();
        const stale =
          !confirming &&
          (!activeTimer.ownerHeartbeat ||
            now.getTime() - activeTimer.ownerHeartbeat >
              this.TIMER_OWNER_STALE_MS);
        if (stale) {
          this.claimOwnership(activeTimer, now);
        } else {
          this.countSecond = this.sharedActiveSeconds(now);
          this.ngZone.run(() => {
            this.entryForm.patchValue({
              timeSpent: this.formatTime(this.countSecond),
            });
          });
          return;
        }
      }

      // Owner tab from here on.
      this.countSecond = this.currentActiveSeconds(now);

      const lastCheckedTime = activeTimer?.lastCheckedTime
        ? new Date(activeTimer.lastCheckedTime)
        : (this.segmentStart ?? now);

      const timeSinceLastCheck = Math.floor(
        (now.getTime() - lastCheckedTime.getTime()) / 1000,
      );

      if (timeSinceLastCheck >= 3600) {
        this.confirmationInProgress = true;

        // Freeze: bank the active seconds up to this boundary and stop the live
        // segment so the dialog window is excluded.
        const frozen = this.currentActiveSeconds(now);
        this.accumulatedSeconds = frozen;
        this.segmentStart = undefined;

        const confirmStartTime = new Date();

        this.ngZone.run(() => {
          this.entryForm.patchValue({ timeSpent: this.formatTime(frozen) });
        });

        localStorage.setItem(
          'confirmationData',
          JSON.stringify({
            confirmationStartTime: confirmStartTime.toISOString(),
            frozenSeconds: frozen,
          }),
        );
        this.saveActiveTimer(frozen, null, lastCheckedTime, false);

        const confirmContinue = await this.handleConfirmationDialog(60);
        if (!confirmContinue) return;

        // Resume: start a fresh segment so the dialog-open time is not counted,
        // and persist it so a reload mid-segment keeps the banked total.
        this.segmentStart = new Date();
        this.confirmationInProgress = false;
        this.saveActiveTimer(
          this.accumulatedSeconds,
          this.segmentStart,
          this.segmentStart,
          false,
        );
        return;
      }

      // Owner heartbeat: refresh liveness (and persist the current segment) so
      // other tabs render the same value and can detect a dead owner.
      this.saveActiveTimer(
        this.accumulatedSeconds,
        this.segmentStart ?? null,
        lastCheckedTime,
        false,
      );

      this.ngZone.run(() => {
        this.entryForm.patchValue({
          timeSpent: this.formatTime(this.countSecond),
        });
      });
    });
  }
  getActiveTimer() {
    const saved = localStorage.getItem('activeTimer');
    if (!saved) return null;
    const parsed = JSON.parse(saved);
    // Migrate legacy schema ({ startTime }) to the accumulated-seconds model.
    if (parsed && parsed.accumulatedSeconds === undefined && parsed.startTime) {
      parsed.accumulatedSeconds = 0;
      parsed.segmentStartTime = parsed.startTime;
    }
    return parsed;
  }

  getConfirmationData() {
    const data = localStorage.getItem('confirmationData');
    return data ? JSON.parse(data) : null;
  }
  checkEntryButton() {
    const selectedTask = this.userMetadata()?.roleTaskMappingResponses.find(
      (t) => t.taskTypeId === this.entryForm.get('taskTypeId')?.value,
    );
    return (
      Object.values(this.attendanceAdjustmentTypeEnum).includes(
        selectedTask?.taskTypeName as AttendanceAdjustmentTypeEnum,
      ) ||
      ((this.selectedTab === 1 || this.selectedTab === 2) && !this.isTimer)
    );
  }
  saveActiveTimer(
    accumulatedSeconds: number,
    segmentStartTime: Date | null,
    lastCheckedTime: Date,
    stopped: boolean,
  ) {
    // The acting tab stamps itself as owner and refreshes the heartbeat. Other
    // tabs use ownerId/ownerHeartbeat to render read-only and detect a dead owner.
    localStorage.setItem(
      'activeTimer',
      JSON.stringify({
        accumulatedSeconds,
        segmentStartTime: segmentStartTime
          ? segmentStartTime.toISOString()
          : null,
        lastCheckedTime: lastCheckedTime.toISOString(),
        isStart: true,
        trackingStopped: stopped,
        ownerId: this.tabId,
        ownerHeartbeat: Date.now(),
      }),
    );
  }

  /** Active seconds derived from the SHARED activeTimer (used by followers and
   * stop paths so every tab agrees on the value regardless of which one owns it). */
  private sharedActiveSeconds(now: Date = new Date()): number {
    const at = this.getActiveTimer();
    if (!at) return this.currentActiveSeconds(now);
    const seg = at.segmentStartTime
      ? Math.max(
          0,
          Math.floor(
            (now.getTime() - new Date(at.segmentStartTime).getTime()) / 1000,
          ),
        )
      : 0;
    return (at.accumulatedSeconds ?? 0) + seg;
  }

  /** A follower promotes itself to owner when the previous owner's tab is gone. */
  private claimOwnership(activeTimer: any, now: Date): void {
    this.isTimerOwner = true;
    this.accumulatedSeconds = activeTimer.accumulatedSeconds ?? 0;
    this.segmentStart = activeTimer.segmentStartTime
      ? new Date(activeTimer.segmentStartTime)
      : now;
    const lastChecked = activeTimer.lastCheckedTime
      ? new Date(activeTimer.lastCheckedTime)
      : now;
    this.saveActiveTimer(
      this.accumulatedSeconds,
      this.segmentStart,
      lastChecked,
      false,
    );
  }

  /** Atomically claim the right to stop+save: returns true only for the first
   * tab to clear activeTimer; later tabs get false and must not save again. */
  private claimStop(): boolean {
    if (!this.getActiveTimer()) return false;
    localStorage.removeItem('activeTimer');
    localStorage.removeItem('confirmationData');
    return true;
  }

  /** Another tab ended the session: stop ticking here and reset UI, no save. */
  private handleExternalStop(): void {
    if (!this.isStart && !this.timerSubscription) return;
    this.timerSubscription?.unsubscribe();
    this.timerSubscription = undefined;
    this.isTimerOwner = false;
    this.isStart = false;
    this.confirmationInProgress = false;
    this.playIcon = PrimeIcons.PLAY;
    this.pTooltipClockIcon = 'Start Timer';
    this.severityClockIcon = null;
    this.ngZone.run(() => {
      this.entryForm.patchValue({ timeSpent: '00:00:00' });
    });
  }

  cleanupTimer() {
    this.timerSubscription?.unsubscribe();
    this.timerSubscription = undefined;
    this.isTimerOwner = false;
    localStorage.removeItem('activeTimer');
    localStorage.removeItem('confirmationData');
  }

  ngOnDestroy(): void {
    // Tear down the in-memory interval so it cannot keep ticking on a destroyed
    // component, but preserve activeTimer/confirmationData in localStorage so a
    // returning user resumes where they left off. Only the owner snapshots state;
    // a follower navigating away must not stamp itself as owner.
    if (
      this.isStart &&
      this.segmentStart &&
      !this.confirmationInProgress &&
      this.isTimerOwner
    ) {
      const existing = this.getActiveTimer();
      const lastChecked = existing?.lastCheckedTime
        ? new Date(existing.lastCheckedTime)
        : this.segmentStart;
      this.saveActiveTimer(
        this.accumulatedSeconds,
        this.segmentStart,
        lastChecked,
        false,
      );
    }
    this.timerSubscription?.unsubscribe();
    this.timerSubscription = undefined;
  }

  async handleConfirmationDialog(timeoutSec: number): Promise<boolean> {
    const userResponse = this.confirmWithDialog(
      'Do you want to continue tracking time?',
      true,
      'Timer Confirmation',
    );

    // Race the dialog against a timeout-resolved promise so an unanswered
    // dialog resolves to "stop" instead of hanging forever.
    let timeoutRef: ReturnType<typeof setTimeout>;
    const timeoutPromise = new Promise<boolean>((resolve) => {
      timeoutRef = setTimeout(() => resolve(false), timeoutSec * 1000);
    });

    const result = await Promise.race([userResponse, timeoutPromise]);
    clearTimeout(timeoutRef!);

    if (result) {
      this.saveActiveTimer(this.accumulatedSeconds, null, new Date(), false);
      localStorage.removeItem('confirmationData');
      return true;
    }

    // No / timed out: dismiss any still-open dialog and stop cleanly.
    this.confirmationService.close();
    await this.stopTrackingAndSave();
    return false;
  }

  /** Stop tracking and persist the entry at the frozen active total. */
  private async stopTrackingAndSave(): Promise<void> {
    // First tab to clear activeTimer wins the save; if another tab already
    // stopped the session, just reset the UI without saving a duplicate.
    const won = this.claimStop();
    this.cleanupTimer();
    this.confirmationInProgress = false;
    this.isStart = false;
    this.playIcon = PrimeIcons.PLAY;
    this.pTooltipClockIcon = 'Start Timer';
    this.severityClockIcon = null;

    if (!won) {
      this.entryForm.patchValue({ timeSpent: '00:00:00' });
      return;
    }

    const now = new Date();
    const total = this.accumulatedSeconds;
    this.entryForm.patchValue({
      timeSpent: this.formatTime(total),
      startTime: new Date(now.getTime() - total * 1000),
      endTime: now,
    });
    await this.onSave();
    this.switchToManualIfNotToday();
  }

  clearEntry(isclearbtn = false, isclockclear = false): void {
    this.entryForm.reset({
      comment: '',
      ticketId: '',
      projectId: isclearbtn ? null : this.entryForm.value.projectId,
      taskTypeId: isclearbtn ? null : this.entryForm.value.taskTypeId,
      startTime:
        !isclearbtn && !this.isTimer ? this.entryForm.value.endTime : null,
      endTime: null,
      taskDate: this.entryForm.value.taskDate,
      checked: false,
      timeSpent: '00:00:00',
    });
    if (
      !this.isTimer &&
      this.userDetails()?.timeCaptureScheme === 'TimeTicking' &&
      this.tabData[this.selectedTab].id === 'today'
    ) {
      this.entryOption();
    }
    this.entryForm.get('projectId')?.setValidators(Validators.required);
    this.entryForm.get('taskTypeId')?.setValidators(Validators.required);
    this.entryForm.get('taskDate')?.setValidators(Validators.required);

    this.manualEntryTime = { start: null, end: null, timeSpent: null };
    if (isclearbtn) {
      this.fieldMeta.ticketId.required = false;
      this.fieldMeta.startTime.required = true;
      this.fieldMeta.endTime.required = true;
      this.fieldMeta.timeSpent.required = true;
      this.fieldMeta.comment.required = false;
      this.commonService.updateFormValidators(
        this.entryForm,
        ['ticketId', 'startTime', 'endTime', 'timeSpent', 'comment'],
        [],
      );
      ['startTime', 'endTime', 'ticketId'].forEach((control) =>
        this.entryForm.get(control)?.enable(),
      );
      this.entryForm.markAsPristine();
      this.entryForm.markAsUntouched();
      this.tasklist = this.userMetadata()?.roleTaskMappingResponses || [];
      this.commonService.showToast('Success', 'Time entry form cleared!');
    }
    this.playIcon = PrimeIcons.PLAY;
    localStorage.setItem('validator', JSON.stringify(this.fieldMeta));
    if (isclockclear) {
      this.isStart = false;
      this.isValidtor = false;
      this.severityClockIcon = null;
      this.entryForm.get('timeSpent')?.setValue('00:00:00');
      this.timerSubscription?.unsubscribe();
      localStorage.removeItem('activeTimer');
      localStorage.removeItem('timeEntry');
    }
    if (!this.isTimer) {
      if (isclearbtn) {
        this.entryForm.get('timeSpent')?.enable();
      }
      this.entryForm.get('timeSpent')?.setValue(null);
    } else {
      this.entryForm.get('timeSpent')?.disable();
      this.entryForm.get('timeSpent')?.setValue('00:00:00');
      this.entryForm.get('timeSpent')?.updateValueAndValidity();
    }
    this.entryForm.updateValueAndValidity({ onlySelf: true, emitEvent: false });
    this.focusInput();

    this.isLeaveProject(false, false);
  }

  calculateTimeDifference(
    start: string | Date,
    end: string | Date,
    isEditmode = false,
    rowData: any = null,
  ): void {
    const s = new Date(start);
    const e = new Date(end);
    const taskDate = new Date(
      isEditmode ? rowData?.taskDate : this.entryForm.value.taskDate,
    );
    const now = new Date();

    if (!start || !end) {
      const zeroTime = '00:00:00';
      if (isEditmode && rowData) {
        rowData['timeSpent'] = zeroTime;
      } else {
        this.entryForm.patchValue({ timeSpent: zeroTime });
      }
      return;
    }

    const startDateTime = new Date(taskDate);
    startDateTime.setHours(s.getHours(), s.getMinutes(), s.getSeconds(), 0);

    const endDateTime = new Date(taskDate);
    endDateTime.setHours(e.getHours(), e.getMinutes(), e.getSeconds(), 0);

    if (endDateTime <= startDateTime) {
      endDateTime.setDate(endDateTime.getDate() + 1);
    }
    if (startDateTime > now) {
      this.commonService.showToast(
        'Error',
        'Invalid time entry: Start time cannot be in the future!',
      );
      if (isEditmode && rowData) {
        rowData['startTime'] = null;
      } else {
        this.entryForm.patchValue({ startTime: null, timeSpent: '00:00:00' });
      }
      return;
    }

    if (endDateTime > now) {
      this.commonService.showToast(
        'Error',
        'Invalid time entry: End time cannot be in the future!',
      );
      if (isEditmode && rowData) {
        rowData['endTime'] = null;
      } else {
        this.entryForm.patchValue({ endTime: null, timeSpent: '00:00:00' });
      }
      return;
    }

    const diffMs = endDateTime.getTime() - startDateTime.getTime();
    const totalSeconds = Math.floor(diffMs / 1000);

    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    const timeSpent = `${this.pad(hours)}:${this.pad(minutes)}:${this.pad(seconds)}`;

    if (hours < 0 || minutes < 0 || seconds < 0) {
      this.commonService.showToast(
        'Error',
        'Start time must be less than end time.',
      );
      rowData['timeSpent'] = '00:00:00';
    }

    if (!isNaN(endDateTime.getTime()) && !isNaN(startDateTime.getTime())) {
      if (isEditmode && rowData) {
        rowData['timeSpent'] = timeSpent;
      } else {
        this.entryForm.patchValue({ timeSpent });
      }
    }
  }

  onTimeInput(
    event: any,
    start: string | Date,
    end: string | Date,
    isEditmode = false,
    rowData: any = null,
  ) {
    const digits = event.target.value.replace(/\D/g, '').slice(0, 6);
    const parts = digits.match(/.{1,2}/g) || [];
    event.target.value = parts.join(':');

    if (digits.length <= 6) {
      this.calculateTimeDifference(start, end, isEditmode, rowData);
    }
  }

  timeBlur(
    event: any,
    isStart: boolean,
    isEditMode = false,
    rowData?: any,
  ): void {
    const rawValue = event.target.value.trim();
    if (!rawValue) return;

    const parts = rawValue.split(':').map((p: string) => parseInt(p, 10));
    let h = 0,
      m = 0,
      s = 0;
    if (parts.length === 3) {
      [h, m, s] = parts;
    } else if (parts.length === 2) {
      [h, m] = parts;
    } else if (parts.length === 1) {
      h = parts[0];
    }

    const isValid = h >= 0 && h < 24 && m >= 0 && m < 60 && s >= 0 && s < 60;

    if (!isValid) {
      this.commonService.showToast(
        'Error',
        'Invalid time format. Please use HH:mm:ss.',
      );
      return;
    }
    if (h < 0 || m < 0 || s < 0) {
      this.commonService.showToast(
        'Error',
        'Time cannot be negative. Please enter a valid time.',
      );
      return;
    }
    const checktime = new Date();
    checktime.setHours(h, m, s, 0);

    const taskDateRaw = isEditMode
      ? rowData.taskDate
      : this.entryForm.get('taskDate')?.value;
    const taskDate = taskDateRaw ? new Date(taskDateRaw) : new Date();

    const time = new Date(taskDate);
    time.setHours(h, m, s, 0);
    event.target.value = `${this.pad(h)}:${this.pad(m)}:${this.pad(s)}`;
    const controlName = isStart ? 'startTime' : 'endTime';
    const now = new Date();

    if (time > now) {
      this.commonService.showToast(
        'Error',
        `Invalid time entry: ${isStart ? 'Start' : 'End'} time cannot be in the future!`,
      );
      if (!isEditMode) {
        this.entryForm.controls[controlName].setValue(null);
      } else if (rowData) {
        rowData[controlName] = null;
        rowData.timeSpent = '00:00:00';
      }
      return;
    }

    if (!isEditMode) {
      this.entryForm.controls[controlName].setValue(time);
      const start = isStart ? time : this.entryForm?.value?.startTime;
      const end = isStart ? this.entryForm?.value?.endTime : time;
      // if (start >= end && end) {
      //   this.commonService.showToast(
      //     'Error',
      //     'Start Time cannot be later than End Time. Please adjust the values!',
      //   );
      //   this.entryForm.controls[controlName].setValue(null);
      //   return;
      // }
      this.calculateTimeDifference(start, end, false, rowData);
    }
    if (rowData) {
      rowData[controlName] = time;
      const taskDate = new Date(rowData.taskDate);

      const startTime = rowData.startTime
        ? this.isHHmmssFormat(rowData.startTime)
          ? (() => {
              const [h, m, s] = rowData.startTime.split(':').map(Number);
              const date = new Date(taskDate);
              date.setHours(h, m, s || 0, 0);
              return date;
            })()
          : this.parseTimeWithTaskDate.call(this, rowData.startTime, taskDate)
        : null;

      const endTime = rowData.endTime
        ? this.isHHmmssFormat(rowData.endTime)
          ? (() => {
              const [h, m, s] = rowData.endTime.split(':').map(Number);
              const date = new Date(taskDate);
              date.setHours(h, m, s || 0, 0);
              return date;
            })()
          : this.parseTimeWithTaskDate.call(this, rowData.endTime, taskDate)
        : null;

      // if (startTime! >= endTime! && endTime!) {
      //   this.commonService.showToast(
      //     'Error',
      //     'Start Time cannot be later than End Time. Please adjust the values!',
      //   );
      //   rowData[controlName] = null;
      //   rowData.timeSpent = '00:00:00';
      //   return;
      // }
      this.calculateTimeDifference(startTime!, endTime!, true, rowData);
    }
  }

  private pad(num: number): string {
    return num < 10 ? '0' + num : num.toString();
  }

  private formatTime(totalSeconds: number): string {
    const hrs = Math.floor(totalSeconds / 3600);
    const mins = Math.floor((totalSeconds % 3600) / 60);
    const secs = totalSeconds % 60;

    const pad = (n: number) => (n < 10 ? '0' + n : n);
    return `${pad(hrs)}:${pad(mins)}:${pad(secs)}`;
  }

  cloneTrack(track: TimeEntry) {
    this.entryForm.patchValue({
      comment: track.comment,
      projectId: track.projectId,
      ticketId: track.ticketId,
      taskTypeId: track.taskTypeId,
      taskDate: new Date(track.taskDate),
      checked: false,
    });
    this.onProjectChange(track.projectId ?? 0);
    this.onTaskChange(track.taskTypeId);
  }
  getZeroTimeError(timeSpent: string): string | null {
    return timeSpent === '00:00:00' ? 'Time Spent cannot be zero.' : null;
  }
  async updateTrack(row: TimeEntry) {
    const selectedTask = this.userMetadata()?.roleTaskMappingResponses.find(
      (t) => t.taskTypeId === row.taskTypeId,
    );

    const errors: string[] = [];
    let errorMsg = null;
    const timeSpentZeroError = this.getZeroTimeError(row.timeSpent);

    if (row.taskDate === null || row.taskDate === undefined) {
      errors.push('Task Date');
    }
    if (row.taskTypeId === null || row.taskTypeId === undefined) {
      errors.push('Task Type');
    }
    if (row.projectId === null || row.projectId === undefined) {
      errors.push('Project & Client');
    }
    if (
      selectedTask?.isTimingRequired &&
      (row.startTime === null ||
        row.startTime === undefined ||
        row.startTime === '')
    ) {
      errors.push('Start Time');
    }

    if (
      selectedTask?.isTimingRequired &&
      (row.endTime === null || row.endTime === undefined || row.endTime === '')
    ) {
      errors.push('End Time');
    }
    if (
      (!selectedTask?.isTimingRequired && row.timeSpent === null) ||
      row.timeSpent === undefined ||
      row.timeSpent === ''
    ) {
      errors.push('Time Spent');
    }
    if (
      selectedTask?.isCommentRequired &&
      (row.comment === null || row.comment === undefined || row.comment === '')
    ) {
      errors.push('Comment');
    }

    const isTicketMissing =
      (!row.ticketId || row.ticketId.trim() === '') &&
      (!row.comment || row.comment.trim() === '');

    if (selectedTask?.isTicketRequired && isTicketMissing) {
      errors.push('Jira Ticket');
    }

    errorMsg = [
      errors.length
        ? `${errors.join(', ')} ${errors.length > 1 ? 'are' : 'is'} required.`
        : '',
      !selectedTask?.isTimingRequired ? timeSpentZeroError : null,
    ]
      .filter(Boolean)
      .join('\n');

    if (errorMsg) {
      this.commonService.showToast('Error', errorMsg);
      return;
    }

    let startTime: string | undefined | null;
    let endTime: string | undefined | null;

    if (row.startTime) {
      startTime = this.isHHmmssFormat(row.startTime)
        ? row.startTime
        : this.commonService['dateService'].format(
            new Date(row.startTime),
            'HH:mm:ss',
          );
    }

    if (row.endTime) {
      endTime = this.isHHmmssFormat(row.endTime)
        ? row.endTime
        : this.commonService['dateService'].format(
            new Date(row.endTime),
            'HH:mm:ss',
          );
    }
    if (
      (startTime && !endTime) ||
      (!startTime && endTime) ||
      (startTime && endTime && startTime === endTime)
    ) {
      this.commonService.showToast(
        'Error',
        'Start and end time are optional for the selected task, but if you enter one, the other is required.',
      );
      return;
    }
    // if (!timingRequired) {
    //   startTime = null;
    //   endTime = null;
    //   row.startTimeEndTime = '';
    // }
    const updateRow = {
      projectId: row.projectId,
      ticketId: row.ticketId,
      taskTypeId: row.taskTypeId,
      taskDate: this.commonService['dateService'].format(
        new Date(row.taskDate),
        'YYYY-MM-DD',
      ),
      startTime: startTime,
      endTime: endTime,
      comment: row.comment,
      timeSpent: row.timeSpent,
    };
    try {
      const timeSpent = this.trackEntryTable
        .filter(
          (x) =>
            x.id !== row.id &&
            x.taskDate ===
              this.dateService.format(new Date(updateRow.taskDate), 'M/D/YYYY'),
        )
        .map((x) => x.timeSpent);
      timeSpent.push(updateRow.timeSpent);
      const selectedTask = this.userMetadata()?.roleTaskMappingResponses.find(
        (t) => t.taskTypeId === updateRow.taskTypeId,
      );
      const totalTimeSpent = this.addMultipleTimes(timeSpent);
      const leaveTypes = Object.values(LeaveTypeEnum)
        .map((type) => type.toLowerCase().trim())
        .filter((type) => type !== 'pto');

      const isLeaveTask = selectedTask?.taskTypeName
        ? leaveTypes.includes(selectedTask.taskTypeName.toLowerCase().trim()) ||
          selectedTask?.taskTypeName === 'PTO'
        : false;

      if (this.getTimePopupLevel(totalTimeSpent) === 'Over 16 hours') {
        await this.confirmWithDialog(
          'You cannot log more than 16 hours in a single day!',
          false,
        );
        return;
      } else if (this.getTimePopupLevel(totalTimeSpent) === 'Over 13 hours') {
        const confirmed = await this.confirmWithDialog(
          "Please double-check your entry to ensure it's accurate. If this is correct, choose <b><u>YES</u></b>. Otherwise, choose <b><u>NO</u></b> and entered data will be saved.",
          true,
        );
        if (!confirmed) {
          return;
        }
      } else if (
        this.getTimePopupLevel(totalTimeSpent) === 'Overtime' &&
        !isLeaveTask
      ) {
        const confirmed = await this.confirmWithDialog(
          "<strong> Heads up!</strong> You've entered more than <strong>" +
            this.userDetails()?.minHours +
            ' hours</strong>. If this includes overtime, please provide a proper comment do you want to proceed.',
          true,
        );
        if (!confirmed) {
          return;
        }
      }
      await this.trackerService.updateTrack(row.id, updateRow);
      const taskDate = new Date(row.taskDate);
      taskDate.setHours(0, 0, 0, 0);
      this.entryForm.patchValue({ taskDate: taskDate });
      this.isLeaveProject(true, false);
      this.commonService.showToast(
        'Success',
        'Time entry updated successfully!',
      );

      await this.loadTabData(this.selectedTab);
    } catch (error: any) {
      const errors = error?.error?.errors;
      if (Array.isArray(errors)) {
        errors.forEach((element: any) => {
          this.commonService.showToast('Error', element);
        });
      }
    }
  }

  cancelEdit(track: TimeEntry) {
    if (track.startTimeEndTime === '') {
      track.startTimeEndTime = '00:00:00 - 00:00:00';
    }
    //Object.assign(track, this.originalRowData);
    this.editingRow = null;
    //this.originalRowData = null;
  }

  editTrack(track: TimeEntry) {
    if (track.startTimeEndTime === '00:00:00 - 00:00:00') {
      track.startTimeEndTime = '';
      track.startTime = null;
      track.endTime = null;
    }
    if (!this.CanEditTimeEntry) return;

    const task = this.userMetadata()?.roleTaskMappingResponses.find(
      (t) => t.taskTypeId === track.taskTypeId,
    )?.isTimingRequired;
    if (!task) {
      this.columns = this.columns.map((col) => {
        if (col.field === 'timeSpent') {
          return {
            ...col,
            type: 'timespent',
          };
        }
        return col;
      });
    } else {
      this.columns = this.columns.map((col) => {
        if (col.field === 'timeSpent') {
          return {
            ...col,
            type: 'readonly',
          };
        }
        return col;
      });
    }
    if (this.editingRow && this.editingRow.id !== track.id) {
      const previousRow = this.originalRowlist.find(
        (item) => item.id === this.editingRow.id,
      );
      if (previousRow) {
        this.cancelEdit(previousRow);
      }
    }

    this.table.initRowEdit(track);
    this.editingRow = track;
    this.clonedRow = JSON.parse(JSON.stringify(track));
    if (this.clonedRow.startTimeEndTime === '00:00:00 - 00:00:00') {
      this.clonedRow.startTime = '';
      this.clonedRow.endTime = '';
      this.clonedRow.startTimeEndTime = '';
    }
    this.onProjectChange(track.projectId ?? 0, true, track);
    this.taskChange(track, track.taskTypeId);
  }

  startEdit(row: any) {
    if (this.editingRow !== row) {
      this.editingRow = row;
    }
  }

  async confirmDelete(newValue: TimeEntry) {
    const message =
      'Are you sure you want to delete this Time entry? This action cannot be undone.';

    this.confirmationService.confirm({
      key: 'dialog',
      message: message,
      header: 'Delete Confirmation',
      accept: async () => {
        await this.onDeleteTrack([newValue.id]);
        this.highlightedDates = [];
        await this.colorCode();
      },
    });
  }

  bulkDelete() {
    const message =
      'Are you sure you want to delete the selected time entry? This action cannot be undone.';

    this.confirmationService.confirm({
      key: 'dialog',
      message: message,
      header: 'Delete Confirmation',
      accept: async () => {
        const ids = this.selectedTrack!.map((item) => item.id);
        await this.onDeleteTrack(ids);
      },
    });
  }

  async onTaskDate(event: any) {
    const req: string[] = [];
    if (this.fieldMeta?.startTime?.required) {
      req.push('startTime');
    }
    if (this.fieldMeta?.endTime?.required) {
      req.push('endTime');
    }
    if (this.fieldMeta?.timeSpent?.required) {
      req.push('timeSpent');
    }
    req.push('taskDate');
    req.push('projectId');
    req.push('taskTypeId');
    this.commonService.updateFormValidators(
      this.entryForm,
      req,
      Validators.required,
    );
    if (this.entryForm.get('taskDate')?.value) {
      this.onDateSelect(this.entryForm.get('taskDate')?.value);
    }
  }
  isSameDate(d1: Date, d2: Date): boolean {
    return (
      d1.getFullYear() === d2.getFullYear() &&
      d1.getMonth() === d2.getMonth() &&
      d1.getDate() === d2.getDate()
    );
  }

  onCalOpen() {
    const today = new Date();
    const match = this.highlightedDates.find((item) =>
      this.isSameDate(new Date(item.date), today),
    );

    if (!match) {
      this.colorCode();
    }
  }
  async onDateSelect(date: Date) {
    const form = this.entryForm;

    if (!date) {
      form.get('startTime')?.reset({ value: null, disabled: false });
      form.get('endTime')?.reset({ value: null, disabled: false });
      return;
    }

    const selected = new Date(date.setHours(0, 0, 0, 0));
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const yesterday = new Date(today);
    const dayOfWeek = yesterday.getDay();
    const lastWorkingDate = new Date(yesterday);
    if (dayOfWeek === 1) {
      lastWorkingDate.setDate(yesterday.getDate() - 3);
    } else if (dayOfWeek === 0) {
      lastWorkingDate.setDate(yesterday.getDate() - 2);
    } else {
      lastWorkingDate.setDate(yesterday.getDate() - 1);
    }

    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - (today.getDay() || 7) + 1);
    startOfWeek.setHours(0, 0, 0, 0);

    let label = 2;
    if (selected.getTime() === today.getTime()) label = 0;
    else if (selected.getTime() === lastWorkingDate.getTime()) label = 1;
    else if (selected >= startOfWeek && selected <= today) label = 2;
    this.selectedTab = label;
    if (this.selectedTab === 2) {
      this.thisWeekData = false;
    }
    this.loadTabData(this.selectedTab);
    const isMeetingTask = this.userMetadata()?.roleTaskMappingResponses.find(
      (task) => task.taskTypeId === this.entryForm.get('taskTypeId')?.value,
    );
    const isMeeting = isMeetingTask?.taskTypeName
      ?.toLowerCase()
      .includes('meeting');

    if (isMeeting && this.entryForm.get('taskDate')?.value) {
      const date = this.entryForm.get('taskDate')?.value as Date;

      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');

      const formattedDate = `${year}-${month}-${day}`;
      this.comments = await this.trackService.getEvents({
        startDate: formattedDate,
        endDate: formattedDate,
      });
    }
    const selectedTask = this.userMetadata()?.roleTaskMappingResponses.find(
      (t) => t.taskTypeId === this.entryForm.get('taskTypeId')?.value,
    );
    this.checkEarlyRelease(selectedTask);
  }

  isHHmmssFormat(value: string): boolean {
    return /^\d{2}:\d{2}:\d{2}$/.test(value);
  }

  projectNameChange(event: SelectChangeEvent, rowData: any) {
    rowData['projectId'] = event.value;
    this.editingRow = structuredClone(rowData);
  }

  parseTimeWithTaskDate = (time: string | Date, taskDate: Date): Date => {
    if (typeof time === 'string' && this.isHHmmssFormat(time)) {
      const [h, m, s] = time.split(':').map(Number);
      const date = new Date(taskDate);
      date.setHours(h, m, s || 0, 0);
      return date;
    }
    const date = new Date(time);
    const corrected = new Date(taskDate);
    corrected.setHours(
      date.getHours(),
      date.getMinutes(),
      date.getSeconds(),
      0,
    );
    return corrected;
  };

  async onTaskChange(event: number | null = null) {
    this.entryForm.get('ticketId')?.clearValidators();
    this.entryForm.get('startTime')?.clearValidators();
    this.entryForm.get('endTime')?.clearValidators();
    const isMeetingTask = this.userMetadata()?.roleTaskMappingResponses.find(
      (task) => task.taskTypeId === this.entryForm.get('taskTypeId')?.value,
    );
    const isMeeting = isMeetingTask?.taskTypeName
      ?.toLowerCase()
      .includes('meeting');
    if (!this.isTimer) {
      this.entryForm.get('timeSpent')?.enable();
      this.entryForm.get('startTime')?.enable();
      this.entryForm.get('endTime')?.enable();
      this.entryForm.get('ticketId')?.enable();
    }
    this.fieldMeta.startTime.required = false;
    this.fieldMeta.endTime.required = false;
    this.fieldMeta.ticketId.required = false;
    this.fieldMeta.timeSpent.required = false;
    this.fieldMeta.comment.required = false;
    const date = this.entryForm.get('taskDate')?.value as Date;
    if (isMeeting && date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const formattedDate = `${year}-${month}-${day}`;
      this.comments = await this.trackService.getEvents({
        startDate: formattedDate,
        endDate: formattedDate,
      });
    }
    const selectedTask = this.userMetadata()?.roleTaskMappingResponses.find(
      (task) => task.taskTypeId === event,
    );
    const isLeaveTask = this.userMetadata()?.roleTaskMappingResponses.find(
      (task) => task.taskTypeId === this.entryForm.get('taskTypeId')?.value,
    );
    const leaveTaskTypes = [
      ...Object.values(LeaveTypeEnum),
      ...Object.values(AttendanceAdjustmentTypeEnum),
    ];
    const isLeave = leaveTaskTypes.some((type) =>
      isLeaveTask?.taskTypeName?.includes(type),
    );
    this.checkEarlyRelease(selectedTask);
    const projectName = this.userMetadata()?.projectMappingResponses.find(
      (x) => x.projectId === this.entryForm.get('projectId')?.value,
    )?.projectName;
    if (!isLeave && projectName?.toLowerCase().includes('leave')) {
      this.entryForm.get('projectId')?.setValue(null);
    }
    const halfDayLeaveTypes = Object.values(HalfDayLeaveTypeEnum);
    const isLeaveTasks = selectedTask?.taskTypeName;
    const isHalfDayLeave =
      isLeaveTasks &&
      halfDayLeaveTypes.includes(isLeaveTasks as HalfDayLeaveTypeEnum);
    const fullDayLeaveTypes = Object.values(FullDayLeaveTypeEnum);
    const isFullDayLeave =
      isLeaveTasks &&
      fullDayLeaveTypes.includes(isLeaveTasks as FullDayLeaveTypeEnum);
    if (isLeave) {
      this.entryForm
        .get('projectId')
        ?.setValue(
          this.userMetadata()?.projectMappingResponses.find((x) =>
            x.projectName?.toLowerCase().includes('leave'),
          )?.projectId,
        );
    }
    if (isHalfDayLeave) {
      this.playIcon = PrimeIcons.SAVE;
      this.severityClockIcon = null;
      this.pTooltipClockIcon = 'Save Entry';
      this.timerSubscription?.unsubscribe();
      this.timerSubscription = undefined;
      localStorage.removeItem('activeTimer');
      this.isStart = false;
      this.entryForm
        .get('timeSpent')
        ?.setValue(
          this.commonService.divideTime(
            this.userService.userDetails()?.minHours || '00:00:00',
          ),
        );
      this.onProjectChange(this.entryForm.get('projectId')?.value ?? 0);
      this.entryForm.get('timeSpent')?.disable();
      this.entryForm.get('startTime')?.disable();
      this.entryForm.get('endTime')?.disable();
      this.entryForm.get('startTime')?.setValue(null);
      this.entryForm.get('endTime')?.setValue(null);
      this.entryForm.get('ticketId')?.disable();
    } else if (isFullDayLeave) {
      this.playIcon = PrimeIcons.SAVE;
      this.severityClockIcon = null;
      this.pTooltipClockIcon = 'Save Entry';
      this.timerSubscription?.unsubscribe();
      this.timerSubscription = undefined;
      localStorage.removeItem('activeTimer');
      this.isStart = false;
      this.entryForm.get('timeSpent')?.setValue(this.userDetails()?.minHours);
      this.entryForm.get('startTime')?.setValue(null);
      this.entryForm.get('startTime')?.disable();
      this.entryForm.get('endTime')?.setValue(null);
      this.entryForm.get('endTime')?.disable();
      this.entryForm.get('timeSpent')?.disable();
      this.entryForm.get('ticketId')?.setValue(null);
      this.entryForm.get('ticketId')?.disable();
      this.onProjectChange(this.entryForm.get('projectId')?.value ?? 0);
    } else {
      if (this.playIcon === PrimeIcons.SAVE) {
        this.playIcon = PrimeIcons.PLAY;
        this.severityClockIcon = null;
        this.pTooltipClockIcon = 'Start Timer';
        this.entryForm.get('timeSpent')?.setValue('00:00:00');
        localStorage.removeItem('activeTimer');
        localStorage.removeItem('timeEntry');
        localStorage.removeItem('activeTimer');
        this.isStart = false;
      }
      const isCorrectTimeSpent =
        this.getTimeDiff(
          this.entryForm.get('startTime')?.value,
          this.entryForm.get('endTime')?.value,
        ) === this.entryForm.get('timeSpent')?.value;
      if (
        !isCorrectTimeSpent &&
        !this.isTimer &&
        selectedTask?.isTimingRequired &&
        this.entryForm.get('startTime')?.value &&
        this.entryForm.get('endTime')?.value
      ) {
        this.entryForm.get('timeSpent')?.setValue('00:00:00');
        this.entryForm.get('startTime')?.setValue(null);
        this.entryForm.get('endTime')?.setValue(null);
      }
      this.entryForm.get('startTime')?.enable();
      this.entryForm.get('endTime')?.enable();
      this.entryForm.get('ticketId')?.enable();
      this.onProjectChange(
        this.entryForm.get('projectId')?.value ?? 0,
        false,
        null,
        true,
      );
    }
    const isTimingRequired = selectedTask?.isTimingRequired;
    const isTicketRequired = selectedTask?.isTicketRequired;
    const isCommentRequired = selectedTask?.isCommentRequired;
    // Clear validators AND refresh each control's validity individually.
    // clearValidators() alone only swaps the validator fn; the control keeps its
    // stale status until updateValueAndValidity() runs on it. A group-level call
    // with onlySelf doesn't propagate down, so fields dropped from the new task's
    // required set would otherwise stay INVALID from the previous task and fail
    // Save with "Please fill all required fields".
    ['startTime', 'endTime', 'ticketId', 'timeSpent', 'comment'].forEach(
      (name) => {
        const control = this.entryForm.get(name);
        control?.clearValidators();
        control?.updateValueAndValidity({ emitEvent: false });
      },
    );
    if (isTicketRequired && isTimingRequired && !isLeave) {
      this.entryForm.get('timeSpent')?.disable();
      if (
        (this.entryForm.get('timeSpent')?.value === '' ||
          this.entryForm.get('timeSpent')?.value === null) &&
        !this.entryForm.get('startTime')?.value &&
        !this.entryForm.get('endTime')?.value
      ) {
        this.entryForm.get('timeSpent')?.setValue('00:00:00');
      }
      this.commonService.updateFormValidators(
        this.entryForm,
        [
          'projectId',
          'taskTypeId',
          'timeSpent',
          'startTime',
          'endTime',
          'taskDate',
          'ticketId',
        ],
        Validators.required,
      );
      this.fieldMeta.startTime.required = true;
      this.fieldMeta.endTime.required = true;
      this.fieldMeta.ticketId.required = true;
      this.fieldMeta.timeSpent.required = false;
    }
    if (isTicketRequired && !isTimingRequired && !isLeave) {
      this.commonService.updateFormValidators(
        this.entryForm,
        ['projectId', 'taskTypeId', 'timeSpent', 'taskDate', 'ticketId'],
        Validators.required,
      );
      if (
        !this.isTimer &&
        this.entryForm.get('timeSpent')?.value === '00:00:00'
      ) {
        this.entryForm.get('timeSpent')?.setValue(null);
      }
      if (!this.isTimer) {
        this.entryForm.get('timeSpent')?.enable();
        // this.entryForm.get('startTime')?.disable();
        //this.entryForm.get('endTime')?.disable();
        // this.entryForm.get('startTime')?.setValue(null);
        //  this.entryForm.get('endTime')?.setValue(null);
      }
      this.fieldMeta.startTime.required = false;
      this.fieldMeta.endTime.required = false;
      this.fieldMeta.ticketId.required = true;
      this.fieldMeta.timeSpent.required = true;
    }
    if (!isTicketRequired && isTimingRequired) {
      this.entryForm.get('timeSpent')?.disable();
      if (
        (this.entryForm.get('timeSpent')?.value === '' ||
          this.entryForm.get('timeSpent')?.value === null) &&
        !this.entryForm.get('startTime')?.value &&
        !this.entryForm.get('endTime')?.value
      ) {
        this.entryForm.get('timeSpent')?.setValue('00:00:00');
      }
      this.commonService.updateFormValidators(
        this.entryForm,
        [
          'projectId',
          'taskTypeId',
          'timeSpent',
          'startTime',
          'endTime',
          'taskDate',
        ],
        Validators.required,
      );
      this.fieldMeta.startTime.required = true;
      this.fieldMeta.endTime.required = true;
      this.fieldMeta.ticketId.required = false;
      this.fieldMeta.timeSpent.required = false;
    }
    if (!isTicketRequired && !isTimingRequired && !isLeave) {
      if (
        !this.isTimer &&
        this.entryForm.get('timeSpent')?.value === '00:00:00'
      ) {
        this.entryForm.get('timeSpent')?.enable();
        this.entryForm.get('timeSpent')?.setValue(null);
      }
      if (!this.isTimer) {
        this.entryForm.get('timeSpent')?.enable();
        // this.entryForm.get('startTime')?.disable();
        // this.entryForm.get('endTime')?.disable();
        // this.entryForm.get('startTime')?.setValue(null);
        // this.entryForm.get('endTime')?.setValue(null);
      }
      this.commonService.updateFormValidators(
        this.entryForm,
        ['projectId', 'taskTypeId', 'timeSpent', 'taskDate'],
        Validators.required,
      );
      this.fieldMeta.startTime.required = false;
      this.fieldMeta.endTime.required = false;
      this.fieldMeta.ticketId.required = false;
      this.fieldMeta.timeSpent.required = true;
    }
    if (isCommentRequired) {
      this.fieldMeta.comment.required = true;
      this.commonService.updateFormValidators(
        this.entryForm,
        ['comment'],
        Validators.required,
      );
    }
    if (this.isTimer) {
      this.fieldMeta.timeSpent.required = true;
    }
    localStorage.setItem('validator', JSON.stringify(this.fieldMeta));
  }
  checkEarlyRelease(selectedTask: any): void {
    const isEarly = Object.values(this.attendanceAdjustmentTypeEnum).includes(
      selectedTask?.taskTypeName as AttendanceAdjustmentTypeEnum,
    );
    const scheme = this.userDetails()?.timeCaptureScheme;
    const isToday = this.tabData[this.selectedTab].id === 'today';
    const timerOn = this.isTimer;

    const isTimeTicking = scheme === 'TimeTicking';
    const isBoth = scheme === 'Both';

    const allowEarly =
      isEarly && ((isTimeTicking && isToday) || (isBoth && timerOn && isToday));

    const allowNormal =
      !isEarly && (isTimeTicking || (isBoth && timerOn)) && !timerOn && isToday;
    if (isEarly && isTimeTicking) {
      this.timerSubscription?.unsubscribe();
      this.timerSubscription = undefined;
      this.isStart = false;
      this.severityClockIcon = null;
      this.playIcon = PrimeIcons.PLAY;
      this.pTooltipClockIcon = 'Start Timer';
      localStorage.removeItem('activeTimer');
    }
    if (allowEarly || allowNormal) this.entryOption();
  }
  onInput(event: any, isEdit = false, rowdata: TimeEntry | null = null): void {
    const digits = event.target.value.replace(/\D/g, '').slice(0, 6);
    const parts = digits.match(/.{1,2}/g) || [];
    event.target.value = parts.join(':');
    if (parts[0] && parseInt(parts[0]) >= 24) {
      this.commonService.showToast(
        'Error',
        'Time spent cannot exceed 24 hours.',
      );
      event.target.value = '';
      this.entryForm.get('timeSpent')?.setValue(null);
      return;
    }

    if (!isEdit) {
      const timeSpentControl = this.entryForm.get('timeSpent');
      timeSpentControl?.setValue(event.target.value, { emitEvent: false });
    } else {
      if (rowdata) {
        rowdata.timeSpent = event.target.value;
      }
    }
  }

  onBlur(rowdata: TimeEntry | null): void {
    let rawValue = '';
    let setValueFn: (val: string) => void = () => null;

    if (rowdata) {
      rawValue = (rowdata.timeSpent || '').trim();
      setValueFn = (val: string) => (rowdata!.timeSpent = val);
    } else {
      const timeSpentControl = this.entryForm.get('timeSpent');
      rawValue = timeSpentControl?.value?.trim() ?? '';
      setValueFn = (val: string) => timeSpentControl?.setValue(val);
    }

    if (!rawValue) return;

    const parts = rawValue.split(':').map((p) => parseInt(p, 10));
    let h = 0,
      m = 0,
      s = 0;

    switch (parts.length) {
      case 3:
        [h, m, s] = parts;
        break;
      case 2:
        [h, m] = parts;
        break;
      case 1:
        h = parts[0];
        break;
      default:
        this.showInvalidTimeError(rowdata);
        return;
    }

    const isValidTime =
      Number.isInteger(h) &&
      h >= 0 &&
      h < 24 &&
      Number.isInteger(m) &&
      m >= 0 &&
      m < 60 &&
      Number.isInteger(s) &&
      s >= 0 &&
      s < 60;

    if (!isValidTime) {
      this.showInvalidTimeError(rowdata);
      return;
    }

    const formatted = [
      h.toString().padStart(2, '0'),
      m.toString().padStart(2, '0'),
      s.toString().padStart(2, '0'),
    ].join(':');

    setValueFn(formatted);
  }

  showInvalidTimeError(target: AbstractControl | TimeEntry | null): void {
    if (!target) return;

    if ('setErrors' in target) {
      target.setErrors({ invalidTime: true });
    }
  }

  handleDropdownChange(event: any, rowData: any, item: any): void {
    const selectedName = event.value;
    const nameKey = item.header === 'Task' ? 'taskTypeId' : 'projectId';
    const nameid = item.header === 'Task' ? 'taskTypeName' : 'projectName';
    const selectedOption = item.options.find(
      (opt: any) => opt[nameid] === selectedName,
    );
    if (selectedOption) {
      rowData[nameKey] = selectedOption[nameKey];
    } else {
      console.warn('No match found for selected name:', selectedName);
    }
  }

  get CanAddTimeEntry() {
    return this.userService.hasPermission(
      PermissionSubject.TimeEntry,
      PermissionAction.Add,
    );
  }

  get CanEditTimeEntry() {
    return (
      this.userDetails()?.timeCaptureScheme !== 'TimeTicking' &&
      this.userService.hasPermission(
        PermissionSubject.TimeEntry,
        PermissionAction.Edit,
      )
    );
  }

  get CanDeleteTimeEntry() {
    return this.userService.hasPermission(
      PermissionSubject.TimeEntry,
      PermissionAction.Remove,
    );
  }

  onDropdownShow() {
    this.dropdownOpen = true;
  }

  onDropdownHide() {
    this.dropdownOpen = false;
  }

  taskChange(
    clonedRow: any,
    taskId: number | null = null,
    rowData: any | null = null,
  ) {
    clonedRow.taskTypeId = taskId;
    const task = this.userMetadata()?.roleTaskMappingResponses.find(
      (t) => t.taskTypeId === taskId,
    );
    if (!task?.isTimingRequired) {
      this.columns = this.columns.map((col) => {
        if (col.field === 'timeSpent') {
          return {
            ...col,
            type: 'timespent',
          };
        }
        return col;
      });
    } else {
      this.columns = this.columns.map((col) => {
        if (col.field === 'timeSpent') {
          return {
            ...col,
            type: 'readonly',
          };
        }
        return col;
      });
    }
    const projectName = this.userMetadata()?.projectMappingResponses.find(
      (x) => x.projectId === clonedRow.projectId,
    )?.projectName;
    const leaveTaskTypes = [
      ...Object.values(LeaveTypeEnum),
      ...Object.values(AttendanceAdjustmentTypeEnum),
    ];
    const leaveTask = leaveTaskTypes.some((type) =>
      task?.taskTypeName?.includes(type),
    );
    const leaveProject = projectName?.toLowerCase().includes('leave');
    if ((leaveTask && !leaveProject) || (!leaveTask && leaveProject)) {
      if (clonedRow) {
        clonedRow.projectId = null;
      }
    }
    const halfDayLeaveTypes = Object.values(HalfDayLeaveTypeEnum);
    const isLeaveTasks = task?.taskTypeName;
    const isHalfDayLeave =
      isLeaveTasks &&
      halfDayLeaveTypes.includes(isLeaveTasks as HalfDayLeaveTypeEnum);
    const fullDayLeaveTypes = Object.values(FullDayLeaveTypeEnum);
    const isFullDayLeave =
      isLeaveTasks &&
      fullDayLeaveTypes.includes(isLeaveTasks as FullDayLeaveTypeEnum);
    if (isHalfDayLeave) {
      this.columns = this.columns.map((col) => {
        if (['ticketId', 'timeSpent', 'startTimeEndTime'].includes(col.field)) {
          return {
            ...col,
            type: 'readonly',
          };
        }
        return col;
      });
      clonedRow.timeSpent = this.commonService.divideTime(
        this.userService.userDetails()?.minHours || '00:00:00',
      );
      clonedRow.startTime = '';
      clonedRow.endTime = '';
      clonedRow.startTimeEndTime = '';
      clonedRow.ticketId = '';

      clonedRow.projectId =
        this.userMetadata()?.projectMappingResponses.find((x) =>
          x.projectName?.toLowerCase().includes('leave'),
        )?.projectId ?? 0;
    } else if (isFullDayLeave) {
      this.columns = this.columns.map((col) => {
        if (['ticketId', 'timeSpent', 'startTimeEndTime'].includes(col.field)) {
          return {
            ...col,
            type: 'readonly',
          };
        }
        return col;
      });
      clonedRow.timeSpent = this.userDetails()?.minHours;
      clonedRow.startTime = '';
      clonedRow.endTime = '';
      clonedRow.startTimeEndTime = '';
      clonedRow.ticketId = '';
      clonedRow.projectId =
        this.userMetadata()?.projectMappingResponses.find((x) =>
          x.projectName?.toLowerCase().includes('leave'),
        )?.projectId ?? 0;
    } else {
      if (task?.isTimingRequired) {
        const isCorrectTimeSpent =
          this.getTimeDiff(clonedRow.startTime, clonedRow.endTime) ===
          clonedRow.timeSpent;
        if (!isCorrectTimeSpent) {
          //clonedRow.timeSpent = '00:00:00';
          clonedRow.startTime = null;
          clonedRow.endTime = null;
          clonedRow.startTimeEndTime = null;
        }
        this.columns = this.columns.map((col) => {
          if (['timeSpent'].includes(col.field)) {
            return {
              ...col,
              type: 'readonly',
            };
          }
          if (['startTimeEndTime'].includes(col.field)) {
            return {
              ...col,
              type: 'timeRange',
            };
          }
          return col;
        });
      } else {
        // clonedRow.startTime = '';
        // clonedRow.endTime = '';
        // clonedRow.startTimeEndTime = '';
        if (clonedRow.timeSpent === '00:00:00') {
          clonedRow.timeSpent = '';
        }
        this.columns = this.columns.map((col) => {
          if (['startTimeEndTime'].includes(col.field)) {
            return {
              ...col,
              type: 'timeRange',
            };
          }
          if (['timeSpent'].includes(col.field)) {
            return {
              ...col,
              type: 'timespent',
            };
          }
          return col;
        });
      }
      this.columns = this.columns.map((col) => {
        if (['ticketId'].includes(col.field)) {
          return {
            ...col,
            type: 'text',
          };
        }
        return col;
      });
    }
  }

  isOverflowing(event: MouseEvent): boolean {
    const el = event.target as HTMLElement;
    return el.scrollWidth > el.clientWidth;
  }

  exportExcel(data: any[]): void {
    this.commonService.exportExcelWithColors(data, this.columns, {
      fileName: 'TrackwiseData.xlsx',
      sheetName: 'My Data',
    });
  }

  getTimeDiff(start: any, end: any): string | null {
    if (!start || !end) return null;

    const toTimeString = (value: any): string => {
      if (typeof value === 'string') return value;
      if (value instanceof Date) {
        const h = value.getHours().toString().padStart(2, '0');
        const m = value.getMinutes().toString().padStart(2, '0');
        const s = value.getSeconds().toString().padStart(2, '0');
        return `${h}:${m}:${s}`;
      }
      return '';
    };

    const toSeconds = (time: string): number => {
      const parts = time.split(':').map(Number);
      if (parts.length !== 3 || parts.some(isNaN)) return NaN;
      const [h, m, s] = parts;
      return h * 3600 + m * 60 + s;
    };

    const startStr = toTimeString(start);
    const endStr = toTimeString(end);

    const startSec = toSeconds(startStr);
    let endSec = toSeconds(endStr);

    if (isNaN(startSec) || isNaN(endSec)) return null;

    if (endSec < startSec) {
      endSec += 24 * 3600;
    }

    const diff = endSec - startSec;
    const h = Math.floor(diff / 3600);
    const m = Math.floor((diff % 3600) / 60);
    const s = diff % 60;

    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  }

  addMultipleTimes(times: string[]): string {
    const normalizeTime = (t: string): string => {
      const parts = t.split(':');
      if (parts.length === 2) {
        return `${t}:00`;
      }
      return t;
    };
    const toSeconds = (t: string) =>
      normalizeTime(t)
        .split(':')
        .reduce((acc, time) => 60 * acc + +time, 0);

    const pad = (n: number) => n.toString().padStart(2, '0');

    const totalSeconds = times.reduce((acc, time) => acc + toSeconds(time), 0);

    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
  }

  getTimePopupLevel(time: string): string | null {
    const toSeconds = (t: string) =>
      t.split(':').reduce((acc, part) => acc * 60 + +part, 0);
    const minHours = this.timeStringToDecimalHours(
      this.userDetails()?.minHours,
    );

    const seconds = toSeconds(time);
    if (seconds > 16 * 3600) {
      return 'Over 16 hours';
    } else if (seconds > 13 * 3600) {
      return 'Over 13 hours';
    } else if (seconds > minHours * 3600) {
      return 'Overtime';
    }

    return null;
  }

  timeStringToDecimalHours(timeStr?: string): number {
    if (!timeStr) return 0;
    const [hours, minutes, seconds] = timeStr.split(':').map(Number);
    return hours + minutes / 60 + (seconds ? seconds / 3600 : 0);
  }
  delayPopup() {
    this.dropdownOpen = true;
    const timeoutMs = 1000;
    setTimeout(() => {
      this.dropdownOpen = false;
    }, timeoutMs);
  }
  confirmWithDialog(
    message: string,
    isCancel: boolean,
    title = 'Time Spent Exceeded',
  ): Promise<boolean> {
    return new Promise((resolve) => {
      this.confirmationService.confirm({
        key: 'overtimeDialog',

        message: message,
        header: title,
        acceptLabel: isCancel ? 'Yes' : 'OK',
        rejectLabel: 'No',
        closable: false,
        rejectVisible: isCancel,
        accept: () => resolve(true),
        reject: () => resolve(false),
      });
    });
  }

  onProjectChange(
    event: number,
    isEditMode = false,
    clonedRow: TimeEntry | null = null,
    isTaskFilter = false,
  ) {
    const task = this.userMetadata()?.roleTaskMappingResponses.find(
      (x) =>
        x.taskTypeId ===
        (isEditMode
          ? clonedRow?.taskTypeId
          : this.entryForm.get('taskTypeId')?.value),
    );
    const projectName = this.userMetadata()?.projectMappingResponses.find(
      (x) => x.projectId === event,
    )?.projectName;
    const leaveTaskTypes = [
      ...Object.values(LeaveTypeEnum),
      ...Object.values(AttendanceAdjustmentTypeEnum),
    ];
    const leaveTask = leaveTaskTypes.some((type) =>
      task?.taskTypeName?.includes(type),
    );

    const leaveProject = projectName?.toLowerCase().includes('leave');
    if ((leaveTask && !leaveProject) || (!leaveTask && leaveProject)) {
      if (isEditMode && clonedRow) {
        clonedRow.taskTypeId = null;
      } else {
        this.fieldMeta.startTime.required = false;
        this.fieldMeta.endTime.required = false;
        this.fieldMeta.timeSpent.required = false;
        this.fieldMeta.ticketId.required = false;
        this.entryForm.get('startTime')?.enable();
        this.entryForm.get('endTime')?.enable();
        this.entryForm.get('ticketId')?.enable();
        if (
          this.userDetails()?.timeCaptureScheme === 'Manual' ||
          (this.userDetails()?.timeCaptureScheme === 'Both' &&
            this.entryBtnIcon === PrimeIcons.STOPWATCH)
        ) {
          this.entryForm.get('startTime')?.setValue(null);
          this.entryForm.get('endTime')?.setValue(null);
          this.entryForm.get('ticketId')?.setValue(null);
          this.entryForm.get('timeSpent')?.setValue(null);
          this.entryForm.get('timeSpent')?.enable();
        } else {
          this.entryForm.get('timeSpent')?.setValue('00:00:00');
        }
        this.manualEntryTime = { start: null, end: null, timeSpent: null };
        this.entryForm.get('taskTypeId')?.setValue(null);
      }
    }
    let filterTask: RoleTaskMappingResponse[] = [];
    if (!projectName && !isTaskFilter) {
      filterTask = this.userMetadata()?.roleTaskMappingResponses || [];
    } else if (leaveProject) {
      filterTask =
        this.userMetadata()?.roleTaskMappingResponses.filter((task) =>
          leaveTaskTypes.some((type) => task.taskTypeName?.includes(type)),
        ) || [];
    } else {
      filterTask =
        this.userMetadata()?.roleTaskMappingResponses.filter(
          (task) =>
            !leaveTaskTypes.some((type) => task.taskTypeName?.includes(type)),
        ) || [];
    }
    if (isEditMode) {
      this.tasklistDropdown = filterTask;
    } else {
      this.tasklist = filterTask;
    }
    this.isLeaveProject(isEditMode, leaveProject);
  }
  isLeaveProject(isEdit = false, isLeaveProject = false) {
    if (isEdit) {
      this.calMaxDate = isLeaveProject ? null : new Date();
      this.maxDate = isLeaveProject ? null : new Date();
      const taskDate = this.clonedRow?.taskDate;
      if (taskDate && this.maxDate && new Date(taskDate) > this.maxDate) {
        this.clonedRow!.taskDate = null;
      }
    } else {
      this.maxDate = isLeaveProject ? null : new Date();
      const taskDate = this.entryForm.get('taskDate')?.value;
      if (taskDate && this.maxDate && new Date(taskDate) > this.maxDate) {
        this.entryForm.get('taskDate')?.setValue(null);
      }
    }
  }

  reqApprovalOpen() {
    this.isApprovalDialogVisible = true;
  }

  reqApprovalClose() {
    this.isApprovalDialogVisible = false;
  }
}
